import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { withUser } from "../Contexts/user";
import queries from "../helpers/queries";
import caseQueries from "../helpers/caseQueries";
import { useMutation, useApolloClient, useQuery } from "@apollo/react-hooks";
import axios from "axios";
import {
    FiEdit,
    FiActivity,
    FiUnlock,
    FiBriefcase,
    FiEye,
    FiFileText,
    FiTrash2,
    FiMail,
} from "react-icons/fi";
import { IoIosAdd } from "react-icons/io";
import moment from "moment";
import Modal from "../Components/modal";
import propertyTypes from "../propertyTypes";

let timerId;
let projectTimerId;
let agencyTimerId;

// Status options for the status tab
const statusOptions = [
    {
        label: "Bank Processing",
        value: "bankProcessing",
    },
    {
        label: "Draft",
        value: "draft",
    },
    {
        label: "Submitted",
        value: "submitted",
    },
    {
        label: "Loan Approved",
        value: "loanApproved",
    },
    {
        label: "Accepted",
        value: "accepted",
    },
    {
        label: "Rejected",
        value: "rejected",
    },
    {
        label: "Cancelled",
        value: "cancelled",
    },
    {
        label: "On Hold",
        value: "onhold",
    },
    {
        label: "Up-Front Reject",
        value: "upfrontReject",
    },
    {
        label: "KIV From Banker",
        value: "kivBanker",
    },
    {
        label: "Pickup Status",
        value: "pickupStatus",
    },
    {
        label: "KIV From Bank HQ",
        value: "kivBankerHQ",
    },
    {
        label: "Recommend From HQ",
        value: "recommendedHQ",
    },
];

const getPropertyAgentValue = (caseRecord) =>
    caseRecord?.propertyAgent || caseRecord?.propertyDetails?.propertyAgent || "";

const mergeCaseBorrower = (incoming, existing) => {
    if (!existing) return incoming;
    const merged = { ...existing, ...incoming };
    Object.keys(existing).forEach((key) => {
        if (incoming[key] === undefined || incoming[key] === null) {
            merged[key] = existing[key];
        }
    });
    return merged;
};

const mergeCaseBorrowers = (updatedBorrowers, prevBorrowers) => {
    if (!updatedBorrowers?.length) return updatedBorrowers;
    if (!prevBorrowers?.length) return updatedBorrowers;
    return updatedBorrowers.map((borrower) => {
        const prevBorrower = prevBorrowers.find((b) => b._id === borrower._id);
        return mergeCaseBorrower(borrower, prevBorrower);
    });
};

const buildBorrowerPayloadFromData = (data, existingBorrower = {}) => ({
    ...(data.borrowerId && { _id: data.borrowerId }),
    name: data.borrowerName ?? existingBorrower.name,
    ic: data.borrowerNric ?? existingBorrower.ic,
    contact: data.borrowerContact ?? existingBorrower.contact,
    email: data.borrowerEmail ?? existingBorrower.email,
    educationLevel:
        data.borrowerEducationLevel ?? existingBorrower.educationLevel,
    smoking: data.borrowerSmoking?.value ?? existingBorrower.smoking ?? "no",
    motherName: data.borrowerMotherName ?? existingBorrower.motherName,
    mailingAddress:
        data.borrowerMailingAddress ?? existingBorrower.mailingAddress,
    allDetails: data.borrowerAllDetails ?? existingBorrower.allDetails,
    salary: data.borrowerSalary
        ? data.borrowerSalary.toString()
        : existingBorrower.salary,
    currentCompanyName:
        data.borrowerCurrentCompanyName ?? existingBorrower.currentCompanyName,
    currentCompanyAddress:
        data.borrowerCurrentCompanyAddress ??
        existingBorrower.currentCompanyAddress,
    currentNatureBusiness:
        data.borrowerCurrentNatureBusiness ??
        existingBorrower.currentNatureBusiness,
    currentHRDirectline:
        data.borrowerCurrentHRDirectline ?? existingBorrower.currentHRDirectline,
    currentPosition: data.borrowerPosition ?? existingBorrower.currentPosition,
    currentYearService: data.currentLengthOfService
        ? data.currentLengthOfService.toString()
        : existingBorrower.currentYearService,
    pastCompanyName:
        data.borrowerPastCompanyName ?? existingBorrower.pastCompanyName,
    pastPosition: data.borrowerPastPosition ?? existingBorrower.pastPosition,
    pastYearService: data.borrowerPastYearService
        ? data.borrowerPastYearService.toString()
        : existingBorrower.pastYearService,
    pastHRDirectline:
        data.borrowerPastHRDirectline ?? existingBorrower.pastHRDirectline,
    pastCompanyAddress:
        data.borrowerPastCompanyAddress ?? existingBorrower.pastCompanyAddress,
    pastNatureBusiness:
        data.borrowerPastNatureBusiness ?? existingBorrower.pastNatureBusiness,
    emergencyName: data.emergencyName ?? existingBorrower.emergencyName,
    emergencyContact:
        data.emergencyContact ?? existingBorrower.emergencyContact,
    emergencyRelationship:
        data.emergencyRelationship ?? existingBorrower.emergencyRelationship,
    emergencyAddress: data.emergencyAddress ?? existingBorrower.emergencyAddress,
    emergencyIC: data.emergencyIC ?? existingBorrower.emergencyIC,
    emergencyOccupation:
        data.emergencyOccupation ?? existingBorrower.emergencyOccupation,
    spouseName: data.spouseName ?? existingBorrower.spouseName,
    spouseContact: data.spouseContact ?? existingBorrower.spouseContact,
    spouseNRIC: data.spouseNRIC ?? existingBorrower.spouseNRIC,
    spouseOccupation:
        data.spouseOccupation ?? existingBorrower.spouseOccupation,
    spouseCompanyName:
        data.spouseCompanyName ?? existingBorrower.spouseCompanyName,
    spouseMonthlyIncome: data.spouseMonthlyIncome
        ? data.spouseMonthlyIncome.toString()
        : existingBorrower.spouseMonthlyIncome,
    childrensNo: data.childrensNo ?? existingBorrower.childrensNo,
    lengthOfStay: data.borrowerLengthOfStay ?? existingBorrower.lengthOfStay,
    maritalStatus:
        data.borrowerMaritalStatus?.value ?? existingBorrower.maritalStatus,
});

const mapCaseBorrowerToBorrowerData = (borrower, prevRow = {}) => {
    const smokingValue = borrower.smoking ?? prevRow.borrowerSmoking?.value;
    const maritalStatusValue =
        borrower.maritalStatus ?? prevRow.borrowerMaritalStatus?.value;

    return {
        borrowerId: borrower._id ?? prevRow.borrowerId,
        borrowerName: borrower.name ?? prevRow.borrowerName,
        borrowerNric: borrower.ic ?? prevRow.borrowerNric,
        borrowerContact: borrower.contact ?? prevRow.borrowerContact,
        borrowerEmail: borrower.email ?? prevRow.borrowerEmail,
        borrowerEducationLevel:
            borrower.educationLevel ?? prevRow.borrowerEducationLevel,
        borrowerSmoking: smokingValue
            ? {
                  label: smokingValue === "yes" ? "YES" : "NO",
                  value: smokingValue,
              }
            : prevRow.borrowerSmoking ?? null,
        borrowerMotherName: borrower.motherName ?? prevRow.borrowerMotherName,
        borrowerMailingAddress:
            borrower.mailingAddress ?? prevRow.borrowerMailingAddress,
        borrowerAllDetails: borrower.allDetails ?? prevRow.borrowerAllDetails,
        borrowerSalary: borrower.salary ?? prevRow.borrowerSalary,
        spouseName: borrower.spouseName ?? prevRow.spouseName,
        spouseNRIC: borrower.spouseNRIC ?? prevRow.spouseNRIC,
        spouseContact: borrower.spouseContact ?? prevRow.spouseContact,
        spouseOccupation: borrower.spouseOccupation ?? prevRow.spouseOccupation,
        spouseCompanyName:
            borrower.spouseCompanyName ?? prevRow.spouseCompanyName,
        spouseMonthlyIncome:
            borrower.spouseMonthlyIncome ?? prevRow.spouseMonthlyIncome,
        childrensNo: borrower.childrensNo ?? prevRow.childrensNo,
        borrowerLengthOfStay:
            borrower.lengthOfStay ?? prevRow.borrowerLengthOfStay,
        currentLengthOfService:
            borrower.currentYearService ?? prevRow.currentLengthOfService,
        borrowerCurrentCompanyName:
            borrower.currentCompanyName ?? prevRow.borrowerCurrentCompanyName,
        borrowerCurrentCompanyAddress:
            borrower.currentCompanyAddress ??
            prevRow.borrowerCurrentCompanyAddress,
        borrowerCurrentNatureBusiness:
            borrower.currentNatureBusiness ??
            prevRow.borrowerCurrentNatureBusiness,
        borrowerCurrentHRDirectline:
            borrower.currentHRDirectline ?? prevRow.borrowerCurrentHRDirectline,
        borrowerPosition: borrower.currentPosition ?? prevRow.borrowerPosition,
        borrowerPastYearService:
            borrower.pastYearService ?? prevRow.borrowerPastYearService,
        borrowerPastCompanyName:
            borrower.pastCompanyName ?? prevRow.borrowerPastCompanyName,
        borrowerPastCompanyAddress:
            borrower.pastCompanyAddress ?? prevRow.borrowerPastCompanyAddress,
        borrowerPastNatureBusiness:
            borrower.pastNatureBusiness ?? prevRow.borrowerPastNatureBusiness,
        borrowerPastHRDirectline:
            borrower.pastHRDirectline ?? prevRow.borrowerPastHRDirectline,
        borrowerPastPosition:
            borrower.pastPosition ?? prevRow.borrowerPastPosition,
        emergencyName: borrower.emergencyName ?? prevRow.emergencyName,
        emergencyContact: borrower.emergencyContact ?? prevRow.emergencyContact,
        emergencyRelationship:
            borrower.emergencyRelationship ?? prevRow.emergencyRelationship,
        emergencyAddress: borrower.emergencyAddress ?? prevRow.emergencyAddress,
        emergencyIC: borrower.emergencyIC ?? prevRow.emergencyIC,
        emergencyOccupation:
            borrower.emergencyOccupation ?? prevRow.emergencyOccupation,
        borrowerMaritalStatus: maritalStatusValue
            ? {
                  label:
                      maritalStatusValue.charAt(0).toUpperCase() +
                      maritalStatusValue.slice(1),
                  value: maritalStatusValue,
              }
            : prevRow.borrowerMaritalStatus ?? null,
    };
};

const EditCase = ({ user }) => {
    const history = useHistory();
    const { register, handleSubmit, control, setValue, getValues, errors } =
        useForm();
    const [activeTab, setActiveTab] = useState("caseInfo");
        // Add state for case data
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Status form control
    const {
        getValues: getValuesStatusForm,
        setValue: setValueStatusForm,
        reset: resetStatusForm,
        control: statusFormControl,
        handleSubmit: handleSubmitStatusForm,
        errors: statusErrors,
    } = useForm({
        defaultValues: {
            status: null,
        },
    });
    
    // Get case ID from URL
    const caseId = window.location.pathname.split('/').pop();
    
    // Query to fetch single case
    const { loading: fetchLoading, error: fetchError } = useQuery(
        caseQueries.FETCH_SINGLE_CASE,
        {
            variables: { id: caseId },
            skip: !caseId,
            onCompleted: (data) => {
                if (data?.fetchSingleCase) {
                    setCaseData(data.fetchSingleCase);
                    setLoading(false);
                }
            },
            onError: (error) => {
                console.error("Error fetching case:", error);
                setLoading(false);
            }
        }
    );

    const apollo = useApolloClient();
    const [borrowers, setBorrowers] = useState([]);
    const [selectedBorrower, setSelectedBorrower] = useState(null);
    const [borrowerData, setBorrowerData] = useState({});
    const [borrowerNoteForm, setBorrowerNoteForm] = useState(false);
    const [borrowerNoteContent, setBorrowerNoteContent] = useState("");
    const [isAllDetailsMode, setIsAllDetailsMode] = useState(false);
    const [createNoteForm, setCreateNoteForm] = useState(false);
    const [noteContent, setNoteContent] = useState("");
    // Add new state for persisting form data
    const [formData, setFormData] = useState(null);
    // Add document-related state
    const [addDocumentModalState, setAddDocumentModalState] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);
    // Add bank-related state
    const [addBankModalState, setAddBankModalState] = useState(false);
    const [caseBankModalState, setCaseBankModalState] = useState(false);
    const [banks, setBanks] = useState([]);
    const [selectedBankId, setSelectedBankId] = useState(null);
    // State for managing sub-emails
    const [addBankSubEmails, setAddBankSubEmails] = useState(['']);
    const [updateBankSubEmails, setUpdateBankSubEmails] = useState([]);
    // Add mail history state
    const [mailHistory, setMailHistory] = useState([]);
    const [mailerState, setMailerState] = useState(false);
    const [addTermModalState, setAddTermModalState] = useState(false);
    const [editTermModalState, setEditTermModalState] = useState(false);
    const [termModalData, setTermModalData] = useState(null);
    const [showTemplate, setShowTemplate] = useState(false);
    const [terms, setTerms] = useState([]);

    // Add separate form state for terms
    const {
        register: termRegister,
        handleSubmit: handleTermSubmit,
        control: termControl,
        reset: resetTermForm,
        errors: termErrors,
    } = useForm({
        defaultValues: {
            plan: "",
            amount: "",
            method: "",
            term: "",
            inforceDate: "",
            status: "active",
        },
    });

    const capitalizeWords = (str) => {
        return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    };

    // Add state for managing lawyer firms
    const [lawyerFirms, setLawyerFirms] = useState([""]);

    // Add functions to handle lawyer firms array
    const addLawyerFirm = () => {
        setLawyerFirms([...lawyerFirms, ""]);
    };

    const removeLawyerFirm = (index) => {
        setLawyerFirms(prevFirms => {
            const newFirms = prevFirms.filter((_, i) => i !== index);
            return newFirms;
        });
    };

    const updateLawyerFirm = (index, value) => {
        const newLawyerFirms = [...lawyerFirms];
        newLawyerFirms[index] = value;
        setLawyerFirms(newLawyerFirms);
    };

    const [updateCase] = useMutation(caseQueries.UPDATE_CASE);
    const [deleteDocument] = useMutation(caseQueries.DELETE_DOCUMENT);
    const [addDocument] = useMutation(caseQueries.ADD_DOCUMENT);
    const [addBank] = useMutation(caseQueries.ADD_BANK);
    const [updateBankStatus] = useMutation(caseQueries.UPDATE_BANK_STATUS);
    const [deleteBank] = useMutation(caseQueries.DELETE_BANK);
    const [sendMail] = useMutation(caseQueries.SEND_EMAIL);
    const [updateCaseStatus] = useMutation(caseQueries.UPDATE_CASE_STATUS);
    const [deleteTerm] = useMutation(caseQueries.DELETE_TERM);
    const [updateTerm] = useMutation(caseQueries.UPDATE_TERM);
    const [createTerm] = useMutation(caseQueries.CREATE_TERM);

    const generateSignature = async () => {
        const ikSigReq = await apollo.query({
            query: queries.GENERATE_IK_SIGNATURE,
            fetchPolicy: "no-cache",
        });

        return ikSigReq;
    };

    const loadActiveUsers = useCallback(
        (val, callback) => {
            clearTimeout(timerId);
            timerId = setTimeout(async () => {
                const result = await apollo.query({
                    query: queries.FETCH_USERS,
                    variables: {
                        name: val,
                        limit: 9999,
                        page: 1,
                        type: "user",
                    },
                    fetchPolicy: "network-only",
                });

                callback([
                    {
                        label: "SELF",
                        value: user.auth.userId,
                    },
                    ...result.data.fetchUsers.data.map((item) => ({
                        label: item.name,
                        value: item._id,
                    })),
                ]);
            }, 500);
        },
        [apollo, user.auth.userId]
    );

    const loadProjects = useCallback(
        (val, callback) => {
            clearTimeout(projectTimerId);
            projectTimerId = setTimeout(async () => {
                const result = await apollo.query({
                    query: caseQueries.FETCH_PROJECTS,
                    variables: {
                        limit: 9999,
                        page: 1,
                        name: val,
                    },
                    fetchPolicy: "network-only",
                });

                callback(
                    result.data.fetchProjects.data.map((item) => ({
                        label: item.name,
                        value: item._id,
                    }))
                );
            }, 500);
        },
        [apollo]
    );

    const loadAgencies = useCallback(
        (val, callback) => {
            clearTimeout(agencyTimerId);
            agencyTimerId = setTimeout(async () => {
                const result = await apollo.query({
                    query: caseQueries.FETCH_AGENCIES,
                    variables: {
                        limit: 9999,
                        page: 1,
                        name: val,
                        status: "active",
                    },
                    fetchPolicy: "network-only",
                });

                callback(
                    result.data.fetchAgencies.data.map((item) => ({
                        label: item.name,
                        value: item._id,
                    }))
                );
            }, 500);
        },
        [apollo]
    );

    const updateBorrowerData = (borrowerId, data) => {
        setBorrowerData((prev) => ({
            ...prev,
            [borrowerId]: {
                ...prev[borrowerId],
                ...data,
            },
        }));
    };

    // Form field definitions
    const createformFields = [
        {
            name: "submissionDate",
            label: "Submission Date",
            type: "date",
            section: "Case Information",
        },
        {
            name: "signedDate",
            label: "Signed Date",
            type: "date",
            section: "Case Information",
        },
        {
            name: "approvalDate",
            label: "Approval Date",
            type: "date",
            section: "Case Information",
        },
        {
            name: "salesType",
            label: "Sales Type (Case)",
            type: "select",
            options: [
                { value: "project", label: "PROJECT" },
                { value: "subsale", label: "SUBSALE" },
                { value: "auction", label: "AUCTION" },
                { value: "refinance", label: "REFINANCE" },
            ],
            section: "Case Information",
        },
        {
            name: "applyFor",
            label: "Apply For",
            type: "select",
            options: [
                { value: "conventional", label: "Conventional" },
                { value: "islamic", label: "Islamic" },
            ],
            section: "Case Information",
        },
        {
            name: "flexiType",
            label: "Flexi Type",
            type: "select",
            options: [
                { value: "full", label: "Fully Flexi" },
                { value: "semi", label: "Semi Flexi" },
            ],
            section: "Case Information",
        },
        // Property Loan Details Section
        {
            name: "propertyAgent",
            label: "Property Agent",
            section: "Property Loan Details",
            required: true,
        },
        {
            name: "unitNumber",
            label: "Unit Number",
            section: "Property Loan Details",
        },
        {
            name: "propertyAddress",
            label: "Full Property Address",
            section: "Property Loan Details",
            type: "textarea",
        },
        {
            name: "propertyRenodetails",
            label: "Renovation Details",
            type: "textarea",
            section: "Property Loan Details",
        },
        {
            name: "propertyType",
            label: "Property Type",
            type: "select",
            options: propertyTypes,
            section: "Property Loan Details",
            isSearchable: true,
        },
        {
            name: "completed",
            label: "Completed / Under con (For Dev Case Only)",
            type: "select",
            options: [
                { value: "completed", label: "COMPLETED" },
                { value: "underCon", label: "UNDER CON" },
            ],
            section: "Property Loan Details",
        },
        {
            name: "bumiStatus",
            label: "Bumi / Non Bumi",
            type: "select",
            options: [
                { value: "bumi", label: "BUMI" },
                { value: "nonbumi", label: "NON BUMI" },
                { value: "malay", label: "MALAY RESERVED LAND" },
            ],
            section: "Property Loan Details",
        },
        {
            name: "propertyHold",
            label: "Property Hold",
            type: "select",
            options: [
                { value: "freehold", label: "FREEHOLD" },
                { value: "leasehold", label: "LEASEHOLD" },
            ],
            section: "Property Loan Details",
        },
        {
            name: "propertySize",
            label: "Build Up Size (SQFT)",
            section: "Property Loan Details",
        },
        {
            name: "landsize",
            label: "Land Size (SQFT)",
            section: "Property Loan Details",
        },
        {
            name: "spaPrice",
            label: "SPA Price (RM)",
            type: "number",
            step: "0.01",
            section: "Property Loan Details",
        },
        {
            name: "marginRequested",
            label: "Margin Requested (%)",
            type: "string",
            section: "Property Loan Details",
        },
        {
            name: "loanAmount",
            label: "Loan Amount (RM)",
            type: "number",
            step: "0.01",
            section: "Property Loan Details",
        },
        {
            name: "loanTenure",
            label: "Loan Tenure (YEARS)",
            type: "number",
            section: "Property Loan Details",
        },
        // Lawyer Firm Section
        {
            name: "spaName",
            label: "SPA Name(Borrower)",
            section: "Lawyer Firm",
            type: "dynamicArray",
            required: true,
        },
        {
            name: "spaLawyer",
            label: "SPA Lawyer Firm",
            section: "Lawyer Firm",
        },
        {
            name: "laLawyer",
            label: "LA(Loan Agreement) Lawyer",
            section: "Lawyer Firm",
        },

        {
            name: "valuerFirm",
            label: "Valuation Firm",
            section: "Lawyer Firm",
        },
        {
            name: "valuerName",
            label: "Valuer Name",
            section: "Lawyer Firm",
        },
        {
            name: "marketValue",
            label: "Market Value (RM)",
            type: "number",
            step: 0.01,
            section: "Lawyer Firm",
        },
        {
            name: "lawyerRemarks",
            label: "Remarks",
            section: "Remarks",
            type: "textarea",
        },
        // Person In Charge section
        {
            name: "agency",
            label: "Agency",
            type: "asyncselect",
            section: "Person In Charge",
            loadOptions: "loadAgencies",
        },
        {
            name: "project",
            label: "Project",
            type: "asyncselect",
            section: "Person In Charge",
            loadOptions: "loadProjects",
        },

        // Emergency Contact section
        {
            name: "emergencyName",
            label: "Full Name",
            section: "Emergency Contact",
            required: true,
            helperText: "(Address Can't Same With Applicant Above)",
        },
        {
            name: "emergencyIC",
            label: "IC",
            section: "Emergency Contact",
        },
        {
            name: "emergencyContact",
            label: "Contact",
            section: "Emergency Contact",
            required: true,
        },
        {
            name: "emergencyOccupation",
            label: "Occupation",
            section: "Emergency Contact",
        },
        {
            name: "emergencyRelationship",
            label: "Relationship",
            section: "Emergency Contact",
            required: true,
        },
        {
            name: "emergencyAddress",
            label: "Address",
            section: "Emergency Contact",
            required: true,
            type: "textarea",
        },
        // Borrower Information section
        {
            name: "borrowerSalary",
            label: "Salary (RM)",
            type: "number",
            step: "0.01",
            section: "Borrower Information",
        },
        {
            name: "borrowerName",
            label: "Borrower Name",
            section: "Borrower Information",
        },
        {
            name: "borrowerNric",
            label: "Borrower NRIC",
            section: "Borrower Information",
        },
        {
            name: "borrowerContact",
            label: "Borrower Contact",
            section: "Borrower Information",
        },
        {
            name: "borrowerEmail",
            label: "Borrower Email",
            section: "Borrower Information",
        },
        {
            name: "borrowerEducationLevel",
            label: "Education Level",
            section: "Borrower Information",
        },
        {
            name: "borrowerSmoking",
            label: "Smoking Status",
            type: "select",
            options: [
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
            ],
            section: "Borrower Information",
        },
        {
            name: "borrowerMotherName",
            label: "Mother's Name",
            section: "Borrower Information",
        },
        {
            name: "borrowerMailingAddress",
            label: "Mailing Address",
            type: "textarea",
            section: "Borrower Information",
        },
        {
            name: "borrowerAllDetails",
            label: "All Details",
            type: "textarea",
            section: "Borrower Information",
        },
        {
            name: "borrowerLengthOfStay",
            label: "Length of Stay (Years)",
            section: "Borrower Information",
        },
        {
            name: "borrowerMaritalStatus",
            label: "Marital Status",
            type: "select",
            options: [
                { value: "married", label: "Married" },
                { value: "single", label: "Single" },
                { value: "divorced", label: "Divorced" },
                { value: "widow", label: "Widow" },
            ],
            section: "Borrower Information",
        },
        {
            name: "spouseName",
            label: "Spouse's Name",
            section: "Borrower Information",
        },
        {
            name: "spouseNRIC",
            label: "Spouse's NRIC",
            section: "Borrower Information",
        },
        {
            name: "spouseContact",
            label: "Spouse's Contact",
            section: "Borrower Information",
        },
        {
            name: "spouseOccupation",
            label: "Spouse's Occupation",
            section: "Borrower Information",
        },
        {
            name: "spouseCompanyName",
            label: "Spouse's Company Name",
            section: "Borrower Information",
        },
        {
            name: "spouseMonthlyIncome",
            label: "Spouse's Monthly Income (RM)",
            type: "number",
            step: "0.01",
            section: "Borrower Information",
        },
        {
            name: "childrensNo",
            label: "No. of Children",
            section: "Borrower Information",
        },

        // Current Employment Details
        {
            name: "borrowerCurrentCompanyName",
            label: "Current Company Name",
            section: "Current Employment Details",
        },
        {
            name: "borrowerCurrentCompanyAddress",
            label: "Current Company Address",
            type: "textarea",
            section: "Current Employment Details",
        },
        {
            name: "borrowerCurrentHRDirectline",
            label: "Current HR Directline",
            section: "Current Employment Details",
        },
        {
            name: "borrowerPosition",
            label: "Current Position",
            section: "Current Employment Details",
        },
        {
            name: "currentLengthOfService",
            label: "Current Date of Joining",
            section: "Current Employment Details",
        },
        {
            name: "borrowerCurrentNatureBusiness",
            label: "Current Nature of Business",
            section: "Current Employment Details",
        },

        // Previous Employment Status

        {
            name: "borrowerPastCompanyName",
            label: "Past Company Name",
            section: "Previous Employment Status",
            helperText:
                "(If Working At Current Company Less Than 1 or 2 Years)",
        },
        {
            name: "borrowerPastCompanyAddress",
            label: "Past Company Address",
            section: "Previous Employment Status",
            type: "textarea",
        },
        {
            name: "borrowerPastHRDirectline",
            label: "Past HR Directline",
            section: "Previous Employment Status",
        },
        {
            name: "borrowerPastPosition",
            label: "Past Position",
            section: "Previous Employment Status",
        },
        {
            name: "borrowerPastYearService",
            label: "Past Length of Service",
            section: "Previous Employment Status",
        },
        {
            name: "borrowerPastNatureBusiness",
            label: "Past Nature of Business",
            section: "Previous Employment Status",
        },
    ];

    // Modify tab switching to save form data
    const handleTabChange = (tabId) => {
        // Check conditions for Terms tab
        if (tabId === "terms") {
            const hasEApplication = documents.some(
                (doc) => doc.type === "e-application"
            );

            const hasSignedLo = documents.some(
                (doc) => doc.type === "signedlo"
            );

            const isLoanApproved =
                caseData?.status === "loanApproved" || caseData?.status === "accepted";

            if (!hasEApplication || !hasSignedLo || !isLoanApproved) {
                alert(
                    "Warning: E-application form, Signed LO document and loan approved status are required before creating terms."
                );
            }
        }

        // Check conditions for Mail History tab
        if (tabId === "mailHistory") {
            const isDraftStatus = caseData?.status === "draft";

            if (isDraftStatus) {
                alert(
                    "Warning: Case status needs to be updated from 'Draft' before sending emails. Please update the case status in the Documents tab first."
                );
            }
        }

        // Save current form data before switching
        if (activeTab === "caseInfo") {
            saveCurrentBorrowerData();
            const currentValues = getValues();
            const currentFormData = {
                ...formData,
                ...currentValues,
                submissionDate: currentValues.submissionDate,
                project: currentValues.project
                    ? {
                          _id: currentValues.project.value,
                          name: currentValues.project.label,
                      }
                    : null,
                agency: currentValues.agency
                    ? {
                          _id: currentValues.agency.value,
                          name: currentValues.agency.label,
                      }
                    : null,
                propertyDetails: {
                    propertyType: currentValues.propertyType?.value,
                    bumi: currentValues.bumi?.value,
                    hold: currentValues.propertyHold?.value,
                    completed: currentValues.completed?.value === "completed",
                    margin: currentValues.marginRequested || 0,
                    renodetails: currentValues.propertyRenodetails,
                    buildup: currentValues.propertySize,
                    address: currentValues.propertyAddress,
                    loanAmount: currentValues.loanAmount || 0,
                    loanTenure: currentValues.loanTenure,
                    spaPrice: currentValues.spaPrice || 0,
                },
                salesType: currentValues.salesType?.value,
                applyFor: currentValues.applyFor?.value,
                flexiType: currentValues.flexiType?.value,
                status: currentValues.status?.value,
                pic: currentValues.salesAgent
                    ? {
                          _id: currentValues.salesAgent.value,
                          name: currentValues.salesAgent.label,
                      }
                    : null,
                mdgAgent: currentValues.mdgAgent
                    ? [
                          {
                              _id: currentValues.mdgAgent.value,
                              name: currentValues.mdgAgent.label,
                          },
                      ]
                    : [],
                borrower: Object.entries(borrowerData).map(([id, data]) =>
                    buildBorrowerPayloadFromData(
                        data,
                        caseData?.borrower?.find(
                            (b) => b._id === data.borrowerId
                        )
                    )
                ),
            };
            setFormData(currentFormData);
        }
        setActiveTab(tabId);
    };

        // Initialize form with case data
    useEffect(() => {
        if (caseData && !loading) {
            setFormData(caseData);

            // Initialize lawyer firms
            if (caseData.spaName) {
                if (Array.isArray(caseData.spaName)) {
                    setLawyerFirms(caseData.spaName);
                } else if (typeof caseData.spaName === "string") {
                    // If it's a string, split by || to handle legacy data
                    setLawyerFirms(
                        caseData.spaName.split("||").map((firm) => firm.trim())
                    );
                } else {
                    setLawyerFirms([""]);
                }
            }

            // Initialize documents only on initial load (when documents state is empty)
            // After that, preserve the documents state to avoid overwriting newly added documents
            if (caseData.document && documents.length === 0) {
                setDocuments(caseData.document);
            }

            // Initialize banks
            if (caseData.bank) {
                setBanks(caseData.bank);
            }

            // Initialize mail history
            if (caseData.mailHistory) {
                setMailHistory(caseData.mailHistory);
            }

            // Initialize terms
            if (caseData.terms) {
                setTerms(caseData.terms);
            }

            // Set case fields
            Object.entries(caseData).forEach(([key, value]) => {
                if (key === "borrower") {
                    // Handle borrowers
                    const borrowersList = value.map((b, index) => ({
                        id: index + 1,
                    }));
                    setBorrowers(borrowersList);
                    setSelectedBorrower(1);

                    // Set borrower data
                    setBorrowerData((prev) => {
                    const borrowerDataObj = {};
                    value.forEach((borrower, index) => {
                        borrowerDataObj[index + 1] = mapCaseBorrowerToBorrowerData(
                            borrower,
                            prev[index + 1] || {}
                        );
                    });
                    return borrowerDataObj;
                    });
                } else if (key === "propertyDetails") {
                    Object.entries(value).forEach(([propKey, propValue]) => {
                        if (propKey === "propertyType") {
                            const propertyTypeOption = propertyTypes.find(
                                (opt) => opt.value === propValue
                            );
                            if (propertyTypeOption) {
                                setValue("propertyType", {
                                    label: propertyTypeOption.label,
                                    value: propertyTypeOption.value,
                                });
                            }
                        } else if (propKey === "bumi") {
                            setValue("bumiStatus", {
                                label:
                                    propValue === "bumi"
                                        ? "BUMI"
                                        : propValue === "nonbumi" ||
                                          propValue === "nonbumi"
                                        ? "NON BUMI"
                                        : propValue === "malay"
                                        ? "MALAY RESERVED LAND"
                                        : undefined,
                                value:
                                    propValue === "bumi"
                                        ? "bumi"
                                        : propValue === "nonbumi" ||
                                          propValue === "nonbumi"
                                        ? "nonbumi"
                                        : propValue === "malay"
                                        ? "malay"
                                        : undefined,
                            });
                        } else if (propKey === "hold") {
                            setValue("propertyHold", {
                                label:
                                    propValue === "freehold"
                                        ? "FREEHOLD"
                                        : propValue === "leasehold"
                                        ? "LEASEHOLD"
                                        : undefined,
                                value: propValue,
                            });
                        } else if (propKey === "completed") {
                            setValue("completed", {
                                label:
                                    propValue === true
                                        ? "COMPLETED"
                                        : propValue === false
                                        ? "UNDER CON"
                                        : undefined,
                                value: propValue,
                            });
                        } else if (propKey === "margin") {
                            setValue("marginRequested", propValue);
                        } else if (propKey === "renodetails") {
                            setValue("propertyRenodetails", propValue);
                        } else if (propKey === "buildup") {
                            setValue("propertySize", propValue);
                        } else if (propKey === "address") {
                            setValue("propertyAddress", propValue);
                        } else if (propKey === "loanAmount") {
                            setValue("loanAmount", propValue);
                        } else if (propKey === "loanTenure") {
                            setValue("loanTenure", propValue);
                        } else if (propKey !== "propertyAgent") {
                            setValue(propKey, propValue);
                        }
                    });
                } else if (key === "status") {
                    setValue("status", {
                        label: value ? value.toUpperCase() : undefined,
                        value: value,
                    });
                } else if (key === "salesType") {
                    setValue("salesType", {
                        label: value ? value.toUpperCase() : undefined,
                        value: value,
                    });
                } else if (key === "applyFor") {
                    setValue("applyFor", {
                        label: value
                            ? value.charAt(0).toUpperCase() + value.slice(1)
                            : undefined,
                        value: value,
                    });
                } else if (key === "flexiType") {
                    setValue("flexiType", {
                        label: value === "full" ? "FULLY FLEXI" : "SEMI FLEXI",
                        value: value,
                    });
                } else if (key === "state") {
                    setValue("state", {
                        label: value,
                        value: value,
                    });
                } else if (key === "pic" && value) {
                    setValue("salesAgent", {
                        label: value.name,
                        value: value._id,
                    });
                } else if (
                    key === "mdgAgent" &&
                    Array.isArray(value) &&
                    value.length > 0
                ) {
                    setValue("mdgAgent", {
                        label: value[0].name ? capitalizeWords(value[0].name) : "None",
                        value: value[0]._id,
                    });

                    if (value[1]) {
                        setValue("nfcAgentOptional", {
                            label: value[1].name ? capitalizeWords(value[1].name) : "None",
                            value: value[1]._id,
                        });
                    }
                } else if (key === "project" && value) {
                    setValue("project", {
                        label: value.name,
                        value: value._id,
                    });
                } else if (key === "agency" && value) {
                    setValue("agency", {
                        label: value.name || value,
                        value: value._id || value,
                    });
                } else {
                    setValue(key, value);
                }
            });

            setValue("propertyAgent", getPropertyAgentValue(caseData));
        }
    }, [caseData, loading, setValue]);

    // Effect to set form values when editing a term
    useEffect(() => {
        if (editTermModalState && termModalData) {
            resetTermForm({
                plan: {
                    value: termModalData.plan,
                    label:
                        termModalData.plan === "ul"
                            ? "Unit Linked"
                            : "Traditional",
                },
                amount: termModalData.amount,
                method: {
                    value: termModalData.method,
                    label:
                        termModalData.method === "monthly"
                            ? "Monthly"
                            : termModalData.method === "quarterly"
                            ? "Quarterly"
                            : termModalData.method === "halfyearly"
                            ? "Half Yearly"
                            : "Yearly",
                },
                term: termModalData.term,
                inforceDate: moment(
                    Number(termModalData.inforceDate) ||
                        termModalData.inforceDate
                ).format("YYYY-MM-DD"),
                autosend: {
                    value: termModalData.autosend,
                    label: termModalData.autosend === true ? "True" : "False",
                },
                status: {
                    value: termModalData.status,
                    label:
                        termModalData.status === "active"
                            ? "Active"
                            : "Inactive",
                },
            });
        }
    }, [editTermModalState, termModalData, resetTermForm]);

    // Effect to restore form data when switching back to case info tab
    useEffect(() => {
        if (activeTab === "caseInfo" && formData) {
            
            // Restore all the form fields
            Object.entries(formData).forEach(([key, value]) => {
                if (key === "propertyDetails") {
                    Object.entries(value).forEach(([propKey, propValue]) => {
                        if (propKey === "propertyType") {
                            const propertyTypeOption = propertyTypes.find(
                                (opt) => opt.value === propValue
                            );
                            if (propertyTypeOption) {
                                setValue("propertyType", {
                                    label: propertyTypeOption.label,
                                    value: propertyTypeOption.value,
                                });
                            }
                        } else if (propKey === "hold") {
                            setValue("propertyHold", {
                                label:
                                    propValue === "freehold"
                                        ? "FREEHOLD"
                                        : propValue === "leasehold"
                                        ? "LEASEHOLD"
                                        : undefined,
                                value: propValue,
                            });
                        } else if (propKey === "bumi" && value) {
                            setValue("bumiStatus", {
                                label:
                                    value.bumi === "bumi"
                                        ? "BUMI"
                                        : value.bumi === "nonbumi" ||
                                          value.bumi === "nonbumi"
                                        ? "NON BUMI"
                                        : value.bumi === "malay"
                                        ? "MALAY RESERVED LAND"
                                        : undefined,
                                value:
                                    value.bumi === "bumi"
                                        ? "bumi"
                                        : value.bumi === "nonbumi" ||
                                          value.bumi === "nonbumi"
                                        ? "nonbumi"
                                        : value.bumi === "malay"
                                        ? "malay"
                                        : undefined,
                            });
                        } else if (propKey === "margin") {
                            setValue("marginRequested", propValue);
                        } else if (propKey === "renodetails") {
                            setValue("propertyRenodetails", propValue);
                        } else if (propKey === "buildup") {
                            setValue("propertySize", propValue);
                        } else if (propKey === "address") {
                            setValue("propertyAddress", propValue);
                        } else if (propKey === "loanAmount") {
                            setValue("loanAmount", propValue);
                        } else if (propKey === "loanTenure") {
                            setValue("loanTenure", propValue);
                        } else if (propKey === "spaPrice") {
                            setValue("spaPrice", propValue);
                        } else if (propKey === "propertyAgent" && propValue) {
                            setValue("propertyAgent", propValue);
                        }
                    });
                } else if (key === "submissionDate") {
                    // Handle both timestamp string and date string formats
                    let date;
                    if (value && value.toString().length > 10) {
                        // Timestamp
                        date = new Date(parseInt(value));
                    } else if (value) {
                        // Already formatted date string
                        date = new Date(value);
                    }
                    if (date && !isNaN(date)) {
                        setValue(
                            "submissionDate",
                            date.toISOString().split("T")[0]
                        );
                    }
                } else if (key === "pic" && value) {
                    setValue("salesAgent", {
                        label: value.name ? capitalizeWords(value.name) : "None",
                        value: value._id,
                    });
                } else if (
                    key === "mdgAgent" &&
                    Array.isArray(value) &&
                    value.length > 0
                ) {
                    setValue("mdgAgent", {
                        label: value[0].name ? capitalizeWords(value[0].name) : "None",
                        value: value[0]._id,
                    });
                } else if (key === "salesType" && value) {
                    setValue("salesType", {
                        label: value ? value.toUpperCase() : undefined,
                        value: value,
                    });
                } else if (key === "applyFor" && value) {
                    setValue("applyFor", {
                        label: value
                            ? value.charAt(0).toUpperCase() + value.slice(1)
                            : undefined,
                        value: value,
                    });
                } else if (key === "flexiType" && value) {
                    setValue("flexiType", {
                        label:
                            value === "full"
                                ? "FULLY FLEXI"
                                : value === "semi"
                                ? "SEMI FLEXI"
                                : undefined,
                        value: value,
                    });
                } else if (key === "status" && value) {
                    setValue("status", {
                        label: value ? value.toUpperCase() : undefined,
                        value: value,
                    });
                } else if (key === "project" && value) {
                    setValue("project", {
                        label: value.name,
                        value: value._id,
                    });
                } else if (key === "agency" && value) {
                    setValue("agency", {
                        label: value.name || value,
                        value: value._id || value,
                    });
                } else if (key === "borrower" && Array.isArray(value)) {
                    setBorrowers(value.map((_, index) => ({ id: index + 1 })));
                    setSelectedBorrower(1);

                    setBorrowerData((prev) => {
                    const borrowerDataObj = {};
                    value.forEach((borrower, index) => {
                        borrowerDataObj[index + 1] = mapCaseBorrowerToBorrowerData(
                            borrower,
                            prev[index + 1] || {}
                        );
                    });
                    return borrowerDataObj;
                    });
                } else {
                    setValue(key, value);
                }
            });

            setValue("propertyAgent", getPropertyAgentValue(formData));
        }
    }, [
        activeTab,
        formData,
        setValue,
        setBorrowers,
        setSelectedBorrower,
        setBorrowerData,
    ]);

    const handleSaveCase = async (formData) => {
        try {
            if (user.auth.type !== "superadmin" && user.auth.type !== "user" && user.auth.type !== "manager") {
                alert("You are not authorized to edit this case");
                return;
            }
            // Save current borrower data before proceeding
            saveCurrentBorrowerData();

            const formValues = getValues();

            if (!formValues.propertyAgent?.trim()) {
                alert("Property Agent is required");
                return;
            }

            // Prepare borrower array from all borrowers
            const borrowerArray = borrowers.map((b) => {
                const data = borrowerData[b.id];
                if (!data) {
                    throw new Error(`Missing data for Borrower #${b.id}`);
                }

                // Validate required fields
                const requiredFields = ["borrowerSalary"];
                const missingFields = requiredFields.filter(
                    (field) => !data[field]
                );

                if (missingFields.length > 0) {
                    throw new Error(
                        `Required fields missing for Borrower #${
                            b.id
                        }: ${missingFields.join(", ")}`
                    );
                }

                return buildBorrowerPayloadFromData(
                    data,
                    caseData?.borrower?.find((b) => b._id === data.borrowerId)
                );
            });

            const result = await updateCase({
                variables: {
                    _id: caseData._id,
                    project: formValues.project?.value,
                    agency: formValues.agency?.value,
                    applyFor: formValues.applyFor?.value,
                    flexiType:
                        formValues.flexiType?.value === "full"
                            ? "full"
                            : "semi",
                    spaName: lawyerFirms,
                    spaLawyer: formValues.spaLawyer || undefined,
                    laLawyer: formValues.laLawyer || undefined,
                    valuerFirm: formValues.valuerFirm,
                    valuerName: formValues.valuerName,
                    marketValue: formValues.marketValue
                        ? formValues.marketValue.toString()
                        : undefined,
                    mdgAgent: formValues.mdgAgent
                        ? formValues.mdgAgent.value
                        : caseData.mdgAgent,
                    nfcAgentOptional: caseData.nfcAgentOptional,
                    createdAt: caseData.createdAt,
                    submissionDate: formValues.submissionDate,
                    signedDate: formValues.signedDate,
                    approvalDate: formValues.approvalDate,
                    status: caseData.status,
                    unitNumber: formValues.unitNumber,
                    state: formValues.state?.value,
                    propertyAgent: formValues.propertyAgent,
                    salesType: formValues.salesType?.value,
                    pic: formValues.salesAgent?.value,
                    lawyerRemarks: formValues.lawyerRemarks,
                    propertyDetails: {
                        address: formValues.propertyAddress,
                        propertyType: formValues.propertyType
                            ? formValues.propertyType.value
                            : undefined,
                        bumi: formValues.bumiStatus
                            ? formValues.bumiStatus?.value === "bumi"
                                ? "bumi"
                                : formValues.bumiStatus?.value === "nonbumi"
                                ? "nonbumi"
                                : formValues.bumiStatus?.value === "malay"
                                ? "malay"
                                : undefined
                            : undefined,

                        hold:
                            formValues.propertyHold &&
                            formValues.propertyHold.value !== null
                                ? formValues.propertyHold.value
                                : undefined,
                        buildup: formValues.propertySize,
                        renodetails: formValues.propertyRenodetails,
                        landsize: formValues.landsize,
                        propertyAgent: formValues.propertyAgent,
                        spaPrice: formValues.spaPrice
                            ? formValues.spaPrice.toString()
                            : undefined,
                        margin: formValues.marginRequested
                            ? formValues.marginRequested
                            : undefined,
                        loanAmount: formValues.loanAmount
                            ? formValues.loanAmount.toString()
                            : undefined,
                        loanTenure: formValues.loanTenure,
                        completed: formValues.completed?.value === "completed",
                    },
                    borrower: borrowerArray,
                },
            });

            if (result.data) {
                setCaseData((prev) => {
                    const updated = result.data.updateCase;
                    if (!updated?.borrower?.length || !prev?.borrower?.length) {
                        return updated;
                    }
                    return {
                        ...updated,
                        borrower: mergeCaseBorrowers(
                            updated.borrower,
                            prev.borrower
                        ),
                    };
                });
                
                // Re-populate form fields with updated data
                const updatedCaseData = result.data.updateCase;
                Object.entries(updatedCaseData).forEach(([key, value]) => {
                    if (key === "propertyDetails") {
                        Object.entries(value).forEach(([propKey, propValue]) => {
                            if (propKey === "propertyType") {
                                const propertyTypeOption = propertyTypes.find(
                                    (opt) => opt.value === propValue
                                );
                                if (propertyTypeOption) {
                                    setValue("propertyType", {
                                        label: propertyTypeOption.label,
                                        value: propertyTypeOption.value,
                                    });
                                }
                            } else if (propKey === "bumi") {
                                setValue("bumiStatus", {
                                    label:
                                        propValue === "bumi"
                                            ? "BUMI"
                                            : propValue === "nonbumi"
                                            ? "NON BUMI"
                                            : propValue === "malay"
                                            ? "MALAY RESERVED LAND"
                                            : undefined,
                                    value: propValue,
                                });
                            } else if (propKey === "hold") {
                                setValue("propertyHold", {
                                    label:
                                        propValue === "freehold"
                                            ? "FREEHOLD"
                                            : propValue === "leasehold"
                                            ? "LEASEHOLD"
                                            : undefined,
                                    value: propValue,
                                });
                            } else if (propKey === "completed") {
                                setValue("completed", {
                                    label:
                                        propValue === true
                                            ? "COMPLETED"
                                            : propValue === false
                                            ? "UNDER CON"
                                            : undefined,
                                    value: propValue,
                                });
                            } else if (propKey === "margin") {
                                setValue("marginRequested", propValue);
                            } else if (propKey === "renodetails") {
                                setValue("propertyRenodetails", propValue);
                            } else if (propKey === "buildup") {
                                setValue("propertySize", propValue);
                            } else if (propKey === "address") {
                                setValue("propertyAddress", propValue);
                            } else if (propKey === "loanAmount") {
                                setValue("loanAmount", propValue);
                            } else if (propKey === "loanTenure") {
                                setValue("loanTenure", propValue);
                            } else if (propKey === "spaPrice") {
                                setValue("spaPrice", propValue);
                            } else if (propKey !== "propertyAgent") {
                                setValue(propKey, propValue);
                            }
                        });
                    } else if (key === "status") {
                        setValue("status", {
                            label: value ? value.toUpperCase() : undefined,
                            value: value,
                        });
                    } else if (key === "salesType") {
                        setValue("salesType", {
                            label: value ? value.toUpperCase() : undefined,
                            value: value,
                        });
                    } else if (key === "applyFor") {
                        setValue("applyFor", {
                            label: value
                                ? value.charAt(0).toUpperCase() + value.slice(1)
                                : undefined,
                            value: value,
                        });
                    } else if (key === "flexiType") {
                        setValue("flexiType", {
                            label: value === "full" ? "FULLY FLEXI" : "SEMI FLEXI",
                            value: value,
                        });
                    } else if (key === "state") {
                        setValue("state", {
                            label: value,
                            value: value,
                        });
                    }
                });

                setValue("propertyAgent", getPropertyAgentValue(updatedCaseData));
                
                alert("Case updated successfully");
            }
        } catch (err) {
            alert(err.message || "Failed to update case");
        }
    };

    const customStyles = useMemo(
        () => ({
            control: (provided, state) => ({
                ...provided,
                borderColor: "#e2e8f0",
                backgroundColor: "#fbfdfe",
                borderRadius: "0.5rem",
                padding: "0.20rem",
                "&:hover": {
                    borderColor: "#e2e8f0",
                },
            }),

            placeholder: (provided, state) => ({
                ...provided,
                color: "#a0aec0",
                fontSize: "0.75rem",
                fontWeight: "600",
            }),

            container: (provided, state) => ({
                ...provided,
                color: "#64748b",
                fontSize: "0.75rem",
                fontWeight: "600",
            }),
        }),
        []
    );

    // Add reset function for the modal
    const resetCreateNoteForm = () => {
        setCreateNoteForm(false);
    };

    // Add reset function with other reset functions
    const resetBorrowerNoteForm = () => {
        setBorrowerNoteForm(false);
    };

    // Add this function to save current borrower data
    const saveCurrentBorrowerData = () => {
        if (!selectedBorrower) return;

        const formValues = getValues();
        const prefix = "borrower";
        const currentData = {
            borrowerName: formValues[`${prefix}Name_${selectedBorrower}`],
            borrowerNric: formValues[`${prefix}Nric_${selectedBorrower}`],
            borrowerContact: formValues[`${prefix}Contact_${selectedBorrower}`],
            borrowerEmail: formValues[`${prefix}Email_${selectedBorrower}`],
            borrowerEducationLevel:
                formValues[`${prefix}EducationLevel_${selectedBorrower}`],
            borrowerSmoking: formValues[`${prefix}Smoking_${selectedBorrower}`],
            borrowerMotherName:
                formValues[`${prefix}MotherName_${selectedBorrower}`],
            borrowerLengthOfStay:
                formValues[`borrowerLengthOfStay_${selectedBorrower}`],
            borrowerMailingAddress:
                formValues[`${prefix}MailingAddress_${selectedBorrower}`],
            borrowerAllDetails:
                formValues[`borrowerAllDetails_${selectedBorrower}`],
            borrowerSalary:
                formValues[`borrowerSalary_${selectedBorrower}`],
            borrowerMaritalStatus:
                formValues[`borrowerMaritalStatus_${selectedBorrower}`],
            spouseName: formValues[`spouseName_${selectedBorrower}`],
            spouseNRIC: formValues[`spouseNRIC_${selectedBorrower}`],
            spouseContact: formValues[`spouseContact_${selectedBorrower}`],
            spouseOccupation:
                formValues[`spouseOccupation_${selectedBorrower}`],
            spouseCompanyName:
                formValues[`spouseCompanyName_${selectedBorrower}`],
            spouseMonthlyIncome:
                formValues[`spouseMonthlyIncome_${selectedBorrower}`],
            childrensNo: formValues[`childrensNo_${selectedBorrower}`],
            currentLengthOfService:
                formValues[`currentLengthOfService_${selectedBorrower}`],
            borrowerCurrentCompanyName:
                formValues[`borrowerCurrentCompanyName_${selectedBorrower}`],
            borrowerCurrentCompanyAddress:
                formValues[`borrowerCurrentCompanyAddress_${selectedBorrower}`],
            borrowerCurrentNatureBusiness:
                formValues[`borrowerCurrentNatureBusiness_${selectedBorrower}`],
            borrowerCurrentHRDirectline:
                formValues[`borrowerCurrentHRDirectline_${selectedBorrower}`],
            borrowerPosition:
                formValues[`borrowerPosition_${selectedBorrower}`],
            borrowerPastCompanyName:
                formValues[`borrowerPastCompanyName_${selectedBorrower}`],
            borrowerPastPosition:
                formValues[`borrowerPastPosition_${selectedBorrower}`],
            borrowerPastYearService:
                formValues[`borrowerPastYearService_${selectedBorrower}`],
            borrowerPastCompanyAddress:
                formValues[`borrowerPastCompanyAddress_${selectedBorrower}`],
            borrowerPastNatureBusiness:
                formValues[`borrowerPastNatureBusiness_${selectedBorrower}`],
            borrowerPastHRDirectline:
                formValues[`borrowerPastHRDirectline_${selectedBorrower}`],
            emergencyName: formValues[`emergencyName_${selectedBorrower}`],
            emergencyIC: formValues[`emergencyIC_${selectedBorrower}`],
            emergencyOccupation:
                formValues[`emergencyOccupation_${selectedBorrower}`],
            emergencyContact:
                formValues[`emergencyContact_${selectedBorrower}`],
            emergencyRelationship:
                formValues[`emergencyRelationship_${selectedBorrower}`],
            emergencyAddress:
                formValues[`emergencyAddress_${selectedBorrower}`],
        };

        // Merge into existing borrower row: getValues() often omits react-select
        // fields (Controller + RHF v5), which would otherwise wipe e.g.
        // borrowerMaritalStatus and borrowerId on full replace.
        setBorrowerData((prev) => {
            const prevRow = prev[selectedBorrower] || {};
            const nextRow = { ...prevRow };
            Object.entries(currentData).forEach(([key, val]) => {
                if (val !== undefined) {
                    nextRow[key] = val;
                }
            });
            return {
                ...prev,
                [selectedBorrower]: nextRow,
            };
        });
    };

    // Modify handleAddBorrower to save current data before adding new borrower
    const handleAddBorrower = () => {
        // Save current borrower data first
        saveCurrentBorrowerData();

        const newBorrowerId =
            borrowers.length > 0
                ? Math.max(...borrowers.map((b) => b.id)) + 1
                : 1;

        setBorrowers((prev) => [...prev, { id: newBorrowerId }]);
        setSelectedBorrower(newBorrowerId);
    };

    // Add effect to populate form fields when switching borrowers
    useEffect(() => {
        if (selectedBorrower && borrowerData[selectedBorrower]) {
            const data = borrowerData[selectedBorrower];
            Object.entries(data).forEach(([key, value]) => {
                setValue(`${key}_${selectedBorrower}`, value);
            });
        }
    }, [selectedBorrower, setValue, borrowerData]);

    // Modify the borrower tab click handler to prevent form submission
    const handleBorrowerTabClick = (e, borrowerId) => {
        e.preventDefault(); // Prevent form submission
        saveCurrentBorrowerData();
        setSelectedBorrower(borrowerId);
    };

    // Modify handleRemoveBorrower to clean up borrower data
    const handleRemoveBorrower = (borrowerId) => {
        if (borrowers.length === 1) return;
        const newBorrowers = borrowers.filter((b) => b.id !== borrowerId);
        setBorrowers(newBorrowers);
        setSelectedBorrower(newBorrowers[0].id);
        setBorrowerData((prev) => {
            const newData = { ...prev };
            delete newData[borrowerId];
            return newData;
        });
    };

    // Add function to handle borrower field changes
    const handleBorrowerFieldChange = (fieldName, value, borrowerId) => {
        setBorrowerData((prev) => ({
            ...prev,
            [borrowerId]: {
                ...prev[borrowerId],
                [fieldName]: value,
            },
        }));
    };

    // Add document handling functions
    const handleDeleteDocument = async (documentId) => {
        if (window.confirm("Are you sure you want to delete this document?")) {
            try {
                            await deleteDocument({
                variables: {
                    caseId: caseData._id,
                    documentId: documentId,
                },
            });
                // Remove document from local state
                setDocuments((prev) =>
                    prev.filter((doc) => doc._id !== documentId)
                );
                alert("Document deleted successfully");
            } catch (error) {
                alert("Failed to delete document");
            }
        }
    };

    const resetAddDocumentModal = () => {
        setAddDocumentModalState(false);
        setIsUploadingDocument(false);
    };

    const handleDocumentUpload = async (
        file,
        documentType,
        documentName,
        bankName
    ) => {
        // Prevent multiple uploads
        if (isUploadingDocument) {
            return;
        }

        setIsUploadingDocument(true);

        try {
            let newImage = undefined;

            if (file) {
                const imageresult = await generateSignature();
                const imageFormData = new FormData();

                imageFormData.append("file", file);
                imageFormData.append(
                    "publicKey",
                    "public_0THMWX6MF/cuVk9EPEFy+e0R0Qk="
                );
                imageFormData.append(
                    "signature",
                    imageresult.data.generateImageKitSignature.signature
                );
                imageFormData.append(
                    "token",
                    imageresult.data.generateImageKitSignature.token
                );
                imageFormData.append(
                    "expire",
                    imageresult.data.generateImageKitSignature.expire
                );
                imageFormData.append("fileName", file.name);
                imageFormData.append("folder", "NFC");

                try {
                    const imageKitRequest = await axios.post(
                        "https://upload.imagekit.io/api/v1/files/upload",
                        imageFormData
                    );
                    newImage = imageKitRequest.data.url;
                } catch (error) {
                    console.log("imagekit error", error);
                    throw new Error("Image upload failed");
                }
            }

            const result = await addDocument({
                variables: {
                    caseId: caseData._id,
                    url: newImage,
                    type: documentType,
                    name: documentName || file.name,
                    bankName: bankName,
                },
            });

            if (result.data && result.data.addDocument) {
                // Update the documents state to reflect the new document in the table
                setDocuments(result.data.addDocument.document);
            }
        } catch (err) {
            console.error("Error adding document:", err);
            alert(
                "Failed to add document: " + (err.message || "Unknown error")
            );
        } finally {
            setIsUploadingDocument(false);
        }
    };



    const renderFormField = (item, borrowerId) => {
        const fieldName = borrowerId ? `${item.name}_${borrowerId}` : item.name;
        const savedValue = borrowerId
            ? borrowerData[borrowerId]?.[item.name]
            : getValues(item.name);

        return (
            <div key={fieldName} className="mb-3">
                <div className="mb-1 text-sm text-lynch font-medium">
                    {item.label}
                </div>
                {item.type === "dynamicArray" ? (
                    <div className="space-y-2">
                        {lawyerFirms.map((value, index) => (
                            <div
                                key={index}
                                className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-2"
                            >
                                <input
                                    className={`p-2 w-full md:flex-1 rounded-sm block bg-white border text-sm font-normal ${
                                        errors[fieldName]
                                            ? "border-red-400"
                                            : "border-gray-300"
                                    }`}
                                    value={value}
                                    onChange={(e) =>
                                        updateLawyerFirm(index, e.target.value)
                                    }
                                    placeholder={`Enter ${item.label}`}
                                    type="text"
                                />
                                <button
                                    type="button"
                                    className="px-3 py-2 bg-black text-white rounded hover:bg-blue-400 hover:text-black"
                                    onClick={() => removeLawyerFirm(index)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="px-3 py-2 bg-black text-white rounded hover:bg-blue-400 hover:text-black"
                            onClick={addLawyerFirm}
                        >
                            Add {item.label}
                        </button>
                    </div>
                ) : item.type === "select" ? (
                    <Controller
                        as={Select}
                        name={fieldName}
                        control={control}
                        defaultValue={savedValue}
                        rules={{ required: false }}
                        styles={customStyles}
                        isSearchable={
                            item.isSearchable ||
                            item.name === "salesAgent" ||
                            item.name === "pic"
                        }
                        options={item.options || []}
                        placeholder={item.label}
                        onChange={([selectedOption]) => {
                            if (borrowerId) {
                                handleBorrowerFieldChange(
                                    item.name,
                                    selectedOption,
                                    borrowerId
                                );
                            }
                            return selectedOption;
                        }}
                    />
                ) : item.type === "multiselect" ? (
                    <Controller
                        as={Select}
                        name={fieldName}
                        control={control}
                        defaultValue={savedValue}
                        rules={{ required: false }}
                        styles={customStyles}
                        isSearchable={true}
                        isMulti={true}
                        options={item.options || []}
                        placeholder={item.label}
                        onChange={([selectedOptions]) => {
                            if (borrowerId) {
                                handleBorrowerFieldChange(
                                    item.name,
                                    selectedOptions,
                                    borrowerId
                                );
                            }
                            return selectedOptions;
                        }}
                    />
                ) : item.type === "asyncselect" ? (
                    <Controller
                        as={AsyncSelect}
                        name={fieldName}
                        control={control}
                        defaultValue={savedValue}
                        rules={{ required: false }}
                        styles={customStyles}
                        isSearchable={true}
                        placeholder={item.label}
                        loadOptions={
                            item.loadOptions === "loadProjects"
                                ? loadProjects
                                : item.loadOptions === "loadAgencies"
                                ? loadAgencies
                                : loadActiveUsers
                        }
                        onChange={([selectedOption]) => {
                            if (borrowerId) {
                                handleBorrowerFieldChange(
                                    item.name,
                                    selectedOption,
                                    borrowerId
                                );
                            }
                            return selectedOption;
                        }}
                    />
                ) : item.type === "textarea" ? (
                    <textarea
                        className={`p-2 w-full rounded-sm block bg-white border text-sm font-normal ${
                            errors[fieldName]
                                ? "border-red-400"
                                : "border-gray-300"
                        }`}
                        name={fieldName}
                        ref={register({
                            required: false,
                        })}
                        defaultValue={savedValue}
                        placeholder={item.label}
                        rows="3"
                        onChange={(e) => {
                            if (borrowerId) {
                                handleBorrowerFieldChange(
                                    item.name,
                                    e.target.value,
                                    borrowerId
                                );
                                // Also update the form field value
                                setValue(fieldName, e.target.value);
                            } else {
                                // Update the form field value for non-borrower fields
                                setValue(fieldName, e.target.value);
                            }
                        }}
                    />
                ) : item.type === "checkbox" ? (
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            name={fieldName}
                            ref={register}
                            defaultChecked={savedValue}
                            className="form-checkbox h-4 w-4 text-blue-600"
                            onChange={(e) => {
                                if (borrowerId) {
                                    handleBorrowerFieldChange(
                                        item.name,
                                        e.target.checked,
                                        borrowerId
                                    );
                                    // Also update the form field value
                                    setValue(fieldName, e.target.checked);
                                }
                            }}
                        />
                        <span className="ml-2 text-sm text-lynch font-medium">
                            {item.label}
                        </span>
                    </label>
                ) : (
                    <input
                        type={item.type || "text"}
                        className={`p-2 w-full rounded-sm block bg-white border text-sm font-normal ${
                            errors[fieldName]
                                ? "border-red-400"
                                : "border-gray-300"
                        }`}
                        name={fieldName}
                        ref={register({
                            required: item.required
                                ? `${item.label} is required`
                                : false,
                        })}
                        defaultValue={savedValue}
                        placeholder={item.label}
                        step={item.type === "number" ? "0.01" : undefined}
                        onChange={(e) => {
                            if (borrowerId) {
                                handleBorrowerFieldChange(
                                    item.name,
                                    e.target.value,
                                    borrowerId
                                );
                                // Also update the form field value
                                setValue(fieldName, e.target.value);
                            } else {
                                // Update the form field value for non-borrower fields
                                setValue(fieldName, e.target.value);
                            }
                        }}
                    />
                )}
                {errors[fieldName] && (
                    <p className="text-red-400 text-xs">{`${item.label} cannot be empty`}</p>
                )}
            </div>
        );
    };

    // Modify handleSubmitBorrowerNoteForm to update borrowerData
    const handleSubmitBorrowerNoteForm = () => {
        const lines = borrowerNoteContent.split("\n");
        const pairValues = [];

        lines.forEach((line) => {
            const [label, value] = line.split(":");
            if (label && value) {
                pairValues.push({
                    label: label.trim(),
                    value: value.trim(),
                });
            }
        });

        setBorrowerNoteForm(false);
        setBorrowerNoteContent("");

        if (pairValues.length > 0) {
            const newBorrowerData = {};

            // Create a mapping between note labels and form field names
            const fieldMapping = {
                NAME: "borrowerName",
                NRIC: "borrowerNric",
                CONTACT: "borrowerContact",
                "EMAIL ADDRESS": "borrowerEmail",
                "EDUCATION LEVEL": "borrowerEducationLevel",
                "SMOKING/NO": "borrowerSmoking",
                "MOTHER NAME": "borrowerMotherName",
                "MAILING ADDRESS": "borrowerMailingAddress",
                "LENGTH OF STAY In this MAILING ADDRESS":
                    "borrowerLengthOfStay",
                "CURRENT COMPANY NAME": "borrowerCurrentCompanyName",
                "CURRENT COMPANY ADDRESS": "borrowerCurrentCompanyAddress",
                "CURRENT COMPANY TELEPHONE": "borrowerCurrentHRDirectline",
                "CURRENT POSITION": "borrowerPosition",
                "CURRENT DATE OF JOINING": "currentLengthOfService",
                "CURRENT NATURE OF BUSINESS": "borrowerCurrentNatureBusiness",
                "PREVIOUS COMPANY NAME": "borrowerPastCompanyName",
                "PREVIOUS ADDRESS": "borrowerPastCompanyAddress",
                "PREVIOUS NATURE OF BUSINESS": "borrowerPastNatureBusiness",
                "PREVIOUS HR DIRECT LINE": "borrowerPastHRDirectline",
                "PREVIOUS YRS IN SERVICE": "borrowerPastYearService",
                "PREVIOUS POSITION": "borrowerPastPosition",
                "MARITAL STATUS": "borrowerMaritalStatus",
                "SALARY": "borrowerSalary",
                "SPOUSE NAME": "spouseName",
                "SPOUSE IC": "spouseNRIC",
                "SPOUSE CONTACT": "spouseContact",
                "SPOUSE OCCUPATION": "spouseOccupation",
                "SPOUSE COMPANY NAME": "spouseCompanyName",
                "SPOUSE MONTHLY INCOME": "spouseMonthlyIncome",
                "NO. CHILDREN": "childrensNo",
                "FULL NAME": "emergencyName",
                "IC NUMBER": "emergencyIC",
                "EMERGENCY CONTACT": "emergencyContact",
                RELATIONSHIP: "emergencyRelationship",
                "HOUSE ADDRESS": "emergencyAddress",
                OCCUPATION: "emergencyOccupation",
            };

            pairValues.forEach((pair) => {
                const baseFieldName = fieldMapping[pair.label];
                if (baseFieldName) {
                    const fieldName = `${baseFieldName}_${selectedBorrower}`;
                    let value = pair.value;

                    // Handle special cases
                    if (baseFieldName === "borrowerSmoking") {
                        value = {
                            label:
                                pair.value.toLowerCase() === "no"
                                    ? "NO"
                                    : "YES",
                            value:
                                pair.value.toLowerCase() === "no"
                                    ? "no"
                                    : "yes",
                        };
                    } else if (baseFieldName === "borrowerMaritalStatus") {
                        value = {
                            label: pair.value,
                            value: pair.value,
                        };
                    }

                    // Update form field
                    setValue(fieldName, value);

                    // Store in borrowerData without the ID suffix
                    newBorrowerData[baseFieldName] = value;
                }
            });

            // Set default salary to 5000 if not provided
            if (!newBorrowerData.borrowerSalary) {
                const salaryFieldName = `borrowerSalary_${selectedBorrower}`;
                setValue(salaryFieldName, 5000);
                newBorrowerData.borrowerSalary = 5000;
            }

            // Update borrowerData state
            updateBorrowerData(selectedBorrower, newBorrowerData);
        }
    };

    // Update the handleSubmitCreateNoteForm function
    const handleSubmitCreateNoteForm = () => {
        if (!noteContent) return;

        const lines = noteContent.split("\n");
        const pairValues = [];

        lines.forEach((line) => {
            const [label, value] = line.split(":");
            if (label && value) {
                pairValues.push({
                    label: label.trim(),
                    value: value.trim(),
                });
            }
        });

        if (pairValues.length > 0) {
            // Open the Add Borrower form

            // Use setTimeout to ensure the form is ready before setting values
            setTimeout(() => {
                // Create a mapping between note labels and form field names
                const fieldMapping = {
                    Project: "project",
                    "Sales Type": "salesType",
                    "Apply For": "applyFor",
                    "Flexi Type": "flexiType",
                    "Unit Number": "unitNumber",
                    "Full Property Address": "propertyAddress",
                    "Renovation Details": "propertyRenodetails",
                    "Project Name": "projectName",
                    "Property Type": "propertyType",
                    Completed: "completionStatus",
                    "Bumi/Non-Bumi": "bumiStatus",
                    "Freehold/Leasehold": "propertyHold",
                    "Land Size": "landsize",
                    "Build Up": "propertySize",
                    "SPA Price(RM)": "spaPrice",
                    "Margin Requested": "marginRequested",
                    "Loan Amount(RM)": "loanAmount",
                    "Loan Tenure": "loanTenure",
                    Remarks: "remarks",
                    "Property Agent": "propertyAgent",
                    Agency: "agency",
                    "SPA Date": "spaDate",
                    "LA Date": "LADate",
                    "SignedLo Date": "LODate",
                    "Valuer Name": "valuerName",
                    "Valuation Firm": "valuerFirm",
                    "Market Value(RM)": "marketValue",
                    "SPA Name(Borrower)": "spaName",
                    "SPA Lawyer Firm": "spaLawyer",
                    "LA Lawyer Firm": "laLawyer",
                };

                // Process each pair and set the corresponding form value
                pairValues.forEach((pair) => {
                    const fieldName = fieldMapping[pair.label];
                    if (fieldName) {
                        // Handle special case for SPA Name (dynamic array)
                        if (fieldName === "spaName") {
                            // Set the SPA Name as the first item in the lawyerFirms array
                            setLawyerFirms([
                                pair.value,
                                ...lawyerFirms.slice(1),
                            ]);
                        } else if (fieldName === "salesType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "applyFor") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "flexiType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "propertyType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "completionStatus") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "bumiStatus") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "propertyHold") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (
                            fieldName === "project" ||
                            fieldName === "agency"
                        ) {
                            // For project and agency, we need to search for the matching option
                            // For now, just set the label and let the user select from dropdown
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value,
                            });
                        } else if (
                            ["spaPrice", "loanAmount", "marketValue"].includes(
                                fieldName
                            )
                        ) {
                            // Remove any currency symbols and convert to number
                            const cleanValue = pair.value.replace(
                                /[^0-9.]/g,
                                ""
                            );
                            setValue(fieldName, cleanValue);
                        } else if (
                            [
                                "marginRequested",
                                "loanTenure",
                                "landSize",
                            ].includes(fieldName)
                        ) {
                            // Convert to number
                            const cleanValue = pair.value.replace(
                                /[^0-9.]/g,
                                ""
                            );
                            setValue(fieldName, cleanValue);
                        } else if (
                            [
                                "caseDraftDate",
                                "submissionDate",
                                "spaDate",
                                "LADate",
                                "LODate",
                            ].includes(fieldName)
                        ) {
                            // Handle date format
                            const date = moment(
                                pair.value,
                                "DD-MM-YYYY"
                            ).format("YYYY-MM-DD");
                            if (date !== "Invalid date") {
                                setValue(fieldName, date);
                            }
                        } else {
                            setValue(fieldName, pair.value);
                        }
                    }
                });
            }, 300); // Give the form 300ms to fully render
        }

        setCreateNoteForm(false);
        setNoteContent(""); // Clear the textarea after submission
    };

    const tabs = [
        { id: "caseInfo", label: "Case Info", icon: FiFileText },
        { id: "caseLogs", label: "Case Logs", icon: FiActivity },
        { id: "banks", label: "Banks", icon: FiUnlock },
        { id: "documents", label: "Documents", icon: FiBriefcase },
        { id: "terms", label: "Terms", icon: FiActivity },
        { id: "mailHistory", label: "Mail History", icon: FiMail },
    ];

    // Add bank handling functions
    const resetAddBankModal = () => {
        setAddBankModalState(false);
        setAddBankSubEmails(['']);
    };

    const resetCaseBankModal = () => {
        setCaseBankModalState(false);
        setSelectedBankId(null);
        setUpdateBankSubEmails([]);
    };

    // Effect to populate updateBankSubEmails when selectedBankId changes
    useEffect(() => {
        if (selectedBankId && banks.length > 0) {
            const selectedBank = banks.find(bank => bank._id === selectedBankId);
            if (selectedBank && selectedBank.bankSubEmail) {
                setUpdateBankSubEmails(selectedBank.bankSubEmail.length > 0 ? selectedBank.bankSubEmail : ['']);
            } else {
                setUpdateBankSubEmails(['']);
            }
        }
    }, [selectedBankId, banks]);

    const handleDeleteBank = async (bankId) => {
        if (window.confirm("Are you sure you want to delete this bank?")) {
            try {
                            await deleteBank({
                variables: {
                    caseId: caseData._id,
                    bankId: bankId,
                },
            });
                // Remove bank from local state
                setBanks((prev) => prev.filter((bank) => bank._id !== bankId));
                alert("Bank deleted successfully");
            } catch (error) {
                alert("Failed to delete bank");
            }
        }
    };

    const handleAddBank = async (bankData) => {
        try {
            const result = await addBank({
                variables: {
                    caseId: caseData._id,
                    ...bankData,
                },
            });

            if (result.data && result.data.addBank) {
                // Update the banks state to reflect the new bank in the table
                setBanks(result.data.addBank.bank);
                
                setAddBankModalState(false);
                alert("Bank added successfully");
            }
        } catch (error) {
            console.error("Error adding bank:", error);
            alert("Failed to add bank: " + (error.message || "Unknown error"));
        }
    };

    const handleUpdateBank = async (bankData) => {
        try {
            const result = await updateBankStatus({
                variables: {
                    caseId: caseData._id,
                    bankId: selectedBankId,
                    ...bankData,
                },
            });

            if (result.data && result.data.updateBankStatus) {
                // Update the banks state to reflect the updated bank in the table
                setBanks(result.data.updateBankStatus.bank);
                
                setCaseBankModalState(false);
                setSelectedBankId(null);
                alert("Bank updated successfully");
            }
        } catch (error) {
            alert("Failed to update bank");
        }
    };

    // Add mail history handling functions
    const resetMailerModal = () => {
        setMailerState(false);
    };

    const handleSendMail = async (mailData) => {
        try {
            sendMail({
                variables: {
                    caseId: caseData._id,
                    email: mailData.receiver,
                    bankId: mailData.bankId,
                },
            });

            setMailerState(false);

            // Show success message
            alert("Email sent, please check back later.");
        } catch (error) {
            console.error("Error sending email:", error);
            alert(
                "Failed to send email: " + (error.message || "Unknown error")
            );
        }
    };

    // Handle status update
    const handleUpdateCaseStatus = async () => {
        const formValues = getValuesStatusForm();

        if (!formValues.status) {
            alert("Please select a status");
            return;
        }

        if (formValues.status.value === caseData.status) {
            alert("No changes detected");
            return;
        }

        // Require at least one uploaded document if status is not draft/cancelled/onhold
        if (
            formValues.status.value !== "draft" &&
            formValues.status.value !== "cancelled" &&
            formValues.status.value !== "onhold"
        ) {
            const uploadedDocsCount = documents?.length || 0;

            if (uploadedDocsCount < 1) {
                alert("Cannot proceed. Please upload at least one document.");
                return;
            }
        }

        // Check required fields for loan approval or submission
        if (
            formValues.status.value === "loanApproved" ||
            formValues.status.value === "submitted"
        ) {
            const requiredFields = [
                { field: "unitNumber", label: "Unit Number" },
                { field: "project", label: "Project" },
                { field: "salesType", label: "Sales Type" },
                {
                    field: "propertyDetails.propertyType",
                    label: "Property Type",
                },
                { field: "propertyDetails.spaPrice", label: "SPA Price" },
                { field: "propertyDetails.loanTenure", label: "Loan Tenure" },
                { field: "propertyDetails.margin", label: "Margin" },
                { field: "propertyDetails.loanAmount", label: "Loan Amount" },
                { field: "applyFor", label: "Apply For" },
                { field: "flexiType", label: "Flexi Type" },
                { field: "borrower", label: "Borrower" },
                { field: "spaName", label: "SPA Name" },
                { field: "spaLawyer", label: "SPA Lawyer" },
                { field: "laLawyer", label: "LA Lawyer" },
            ];

            const missingFields = requiredFields.filter(({ field }) => {
                const value = field.includes(".")
                    ? field
                          .split(".")
                          .reduce(
                              (obj, key) => obj?.[key],
                              caseData
                          )
                    : caseData[field];

                return (
                    !value ||
                    (Array.isArray(value) && value.length === 0) ||
                    (typeof value === "object" &&
                        Object.keys(value).length === 0)
                );
            });
        }

        try {
            const result = await updateCaseStatus({
                variables: {
                    id: caseData._id,
                    status: formValues.status.value,
                },
            });

            if (result.data) {
                // Update case data while preserving documents from state
                setCaseData(prev => ({
                    ...prev,
                    status: result.data.updateCaseStatus.status,
                    log: result.data.updateCaseStatus.log,
                    document: documents.length > 0 ? documents : prev.document // Use current documents state if available
                }));

                // Update the status form field to reflect the new status
                const newStatus = result.data.updateCaseStatus.status;
                const statusOption = statusOptions.find(
                    (opt) => opt.value === newStatus
                );
                if (statusOption) {
                    setValueStatusForm("status", {
                        label: statusOption.label,
                        value: statusOption.value,
                    });
                }

                alert("Status updated successfully");
                resetStatusForm();
            }
        } catch (error) {
            console.error("Error updating status:", error);
            alert(
                "Failed to update status: " + (error.message || "Unknown error")
            );
        }
    };

    const handleAddTerm = async (termData) => {
        try {
            const result = await createTerm({
                variables: {
                    input: {
                        case: caseData._id,
                        borrower: termData.borrower
                            ? termData.borrower.value
                            : undefined,
                        policyNumber: termData.policyNumber
                            ? termData.policyNumber
                            : undefined,
                        amount: termData.amount,
                        plan: termData.plan ? termData.plan.value : undefined,
                        method: termData.method
                            ? termData.method.value
                            : undefined,
                        inforceDate: termData.inforceDate
                            ? moment(termData.inforceDate).format("YYYY-MM-DD")
                            : undefined,
                        createdAt: termData.createdAt
                            ? moment(termData.createdAt).format("YYYY-MM-DD")
                            : undefined,
                        manualYear: undefined,
                        status: "active",
                        term: termData.term,
                    },
                },
            });


            if (result.data && result.data.createTerm) {
                // Update the terms state to reflect the new term in the table
                setTerms(prev => [...prev, result.data.createTerm]);
                
                setAddTermModalState(false);
                alert("Term added successfully");
            }
        } catch (error) {
            console.error("Error adding term:", error);
            alert("Failed to add term: " + (error.message || "Unknown error"));
        }
    };

    const handleDeleteTerm = async (termId) => {
        if (window.confirm("Are you sure you want to delete this term?")) {
            try {
                await deleteTerm({
                    variables: {
                        _id: termId,
                    },
                });
                // Remove term from local state
                setTerms((prev) => prev.filter((term) => term._id !== termId));
                alert("Term deleted successfully");
            } catch (error) {
                alert("Failed to delete term");
            }
        }
    };

    const handleEditTerm = async (termData) => {
        try {
            // Check if inforceDate is 58 days before today

            if (termData.inforceDate && termData.status.value === "active") {
                const inforceDate = moment(termData.inforceDate).startOf('day');
                const diffDays = moment().startOf('day').diff(inforceDate, 'days');
                if (diffDays > 58) {
                    alert("Inforce date cannot be 58 days before today");
                    return;
                }
            }
            
            const result = await updateTerm({
                variables: {
                    _id: termModalData._id,
                    autosend: termData.autosend
                        ? termData.autosend.value
                        : termModalData.autosend,
                    plan: termData.plan
                        ? termData.plan.value
                        : termModalData.plan,
                    amount:
                        termData.amount !== undefined
                            ? termData.amount
                            : termModalData.amount,
                    method: termData.method
                        ? termData.method.value
                        : termModalData.method,
                    inforceDate: termData.inforceDate
                        ? moment(termData.inforceDate).format("YYYY-MM-DD")
                        : termModalData.inforceDate,
                    term:
                        termData.term !== undefined
                            ? parseInt(termData.term)
                            : parseInt(termModalData.term),
                    status: termData.status
                        ? termData.status.value
                        : termModalData.status,
                    borrower: termData.borrower
                        ? termData.borrower.value
                        : termModalData.borrower,
                    policyNumber:
                        termData.policyNumber !== undefined
                            ? termData.policyNumber
                            : termModalData.policyNumber,
                    case: caseData._id,
                },
            });

            if (result.data && result.data.updateTerm) {
                // Update the terms state to reflect the updated term in the table
                setTerms(prevTerms => {
                    const newTerms = [...prevTerms];
                    const idx = newTerms.findIndex(term => term._id === result.data.updateTerm._id);
                    if (idx !== -1) {
                        newTerms[idx] = result.data.updateTerm;
                    }
                    return newTerms;
                });

                setEditTermModalState(false);
                setTermModalData(null);
                alert("Term updated successfully");
            }
        } catch (error) {
            console.error("Error editing term:", error);
            alert("Failed to edit term: " + (error.message || "Unknown error"));
        }
    };

    const resetAddTermModal = () => {
        setAddTermModalState(false);
        resetTermForm();
    };

    const resetEditTermModal = () => {
        setEditTermModalState(false);
        setTermModalData(null);
        resetTermForm();
    };



    // Show loading state while data is being fetched
    if (loading || fetchLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading case data...</div>
            </div>
        );
    }

    // Show error state if there's an error
    if (fetchError) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-red-600">Error loading case data: {fetchError.message}</div>
            </div>
        );
    }

    // Show message if no case data is available
    if (!caseData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">No case data found.</div>
            </div>
        );
    }

    return (
        <div className="py-4 px-4 md:px-12">
            {/* Header Actions */}
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={() => history.push("/cases")}
                    className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H17a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <span>Back To List</span>
                </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <div className="flex space-x-4 md:space-x-8 overflow-x-auto pb-2 md:pb-0">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`flex items-center pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                activeTab === tab.id
                                    ? "border-blue-500 text-blue-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                            onClick={() => handleTabChange(tab.id)}
                        >
                            <tab.icon className="w-5 h-5 mr-2" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow">
                {activeTab === "caseInfo" && (
                    <div className="p-6">
                        <form onSubmit={handleSubmit(handleSaveCase)}>
                            {/* Case Information Section */}
                            <div className="mb-6">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium mb-2 md:mb-0">
                                        Case Information
                                    </h3>
                                    <div className="hidden md:flex">
                                        <button
                                            type="button"
                                            className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                            onClick={() => setCreateNoteForm(true)}
                                        >
                                            <FiFileText className="w-4 h-4 mr-1" />
                                            <span>Auto Fill</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="block md:hidden mb-4">
                                    <button
                                        type="button"
                                        className="w-full px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                        onClick={() => setCreateNoteForm(true)}
                                    >
                                        <FiFileText className="w-4 h-4 mr-1" />
                                        <span>Auto Fill</span>
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section ===
                                                "Case Information"
                                        )
                                        .map((item) => renderFormField(item))}
                                </div>
                            </div>

                            {/* Person In Charge Section */}
                            <div className="mb-6">
                                <div className="flex items-center bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Person In Charge
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section ===
                                                "Person In Charge"
                                        )
                                        .map((item) => renderFormField(item))}
                                    <div className="w-full mb-3">
                                        <div className="mb-1 text-sm text-lynch font-medium">
                                            NFC Agent
                                        </div>
                                        <Controller
                                            as={
                                                <AsyncSelect
                                                    styles={customStyles}
                                                />
                                            }
                                            name="mdgAgent"
                                            control={control}
                                            rules={{ required: true }}
                                            loadOptions={loadActiveUsers}
                                        />
                                        {errors.mdgAgent && (
                                            <p className="text-red-400 text-xs">
                                                NFC Agent cannot be empty
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Property Loan Details Section */}
                            <div className="mb-6">
                                <div className="bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Property Loan Details
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section ===
                                                "Property Loan Details"
                                        )
                                        .map((item) => renderFormField(item))}
                                </div>
                            </div>

                            {/* Borrower Information Section */}
                            <div className="mb-6">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-orange-400 text-white p-2 mb-4">
                                    <div className="flex items-center gap-3 mb-2 md:mb-0">
                                        <h3 className="text-base font-medium">
                                            Borrower Information
                                        </h3>
                                        <button
                                            type="button"
                                            className={`px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide border transition-all duration-200 ${
                                                isAllDetailsMode
                                                    ? "bg-teal-600 border-teal-600 text-white shadow-sm hover:bg-teal-700"
                                                    : "bg-yellow-100 border-yellow-300 text-gray-800 hover:bg-yellow-200"
                                            }`}
                                            onClick={() =>
                                                setIsAllDetailsMode((prev) => !prev)
                                            }
                                        >
                                            {isAllDetailsMode
                                                ? "All Details Mode: ON"
                                                : "All Details Mode: OFF"}
                                        </button>
                                    </div>
                                    <div className="hidden md:flex items-center space-x-4">
                                        <button
                                            type="button"
                                            className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                            onClick={() =>
                                                setBorrowerNoteForm(true)
                                            }
                                        >
                                            <FiFileText className="w-4 h-4 mr-1" />
                                            <span>Auto Fill</span>
                                        </button>
                                        <div className="flex flex-wrap space-x-2">
                                            {borrowers.map((borrower) => (
                                                <button
                                                    key={borrower.id}
                                                    type="button"
                                                    className={`px-3 py-1 rounded-lg text-sm ${
                                                        selectedBorrower ===
                                                        borrower.id
                                                            ? "bg-indigo-700 text-white shadow-md"
                                                            : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                                    }`}
                                                    onClick={(e) =>
                                                        handleBorrowerTabClick(
                                                            e,
                                                            borrower.id
                                                        )
                                                    }
                                                >
                                                    Borrower #{borrower.id}
                                                    {borrowers.length > 1 && (
                                                        <span
                                                            className="ml-2 hover:text-red-300"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveBorrower(
                                                                    borrower.id
                                                                );
                                                            }}
                                                        >
                                                            ×
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                                onClick={handleAddBorrower}
                                            >
                                                Add Borrower
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="block md:hidden mb-4">
                                    <div className="flex flex-col space-y-2">
                                        <button
                                            type="button"
                                            className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                            onClick={() =>
                                                setBorrowerNoteForm(true)
                                            }
                                        >
                                            <FiFileText className="w-4 h-4 mr-1" />
                                            <span>Auto Fill</span>
                                        </button>
                                        <div className="flex flex-wrap gap-2">
                                            {borrowers.map((borrower) => (
                                                <button
                                                    key={borrower.id}
                                                    type="button"
                                                    className={`px-3 py-1 rounded-lg text-sm ${
                                                        selectedBorrower ===
                                                        borrower.id
                                                            ? "bg-indigo-700 text-white shadow-md"
                                                            : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                                    }`}
                                                    onClick={(e) =>
                                                        handleBorrowerTabClick(
                                                            e,
                                                            borrower.id
                                                        )
                                                    }
                                                >
                                                    Borrower #{borrower.id}
                                                    {borrowers.length > 1 && (
                                                        <span
                                                            className="ml-2 hover:text-red-300"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveBorrower(
                                                                    borrower.id
                                                                );
                                                            }}
                                                        >
                                                            ×
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                                onClick={handleAddBorrower}
                                            >
                                                Add Borrower
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section === "Borrower Information" &&
                                                (isAllDetailsMode
                                                    ? [
                                                          "borrowerSalary",
                                                          "borrowerName",
                                                          "borrowerAllDetails",
                                                      ].includes(field.name)
                                                    : field.name !==
                                                      "borrowerAllDetails")
                                        )
                                        .map((item) =>
                                            renderFormField(
                                                item,
                                                selectedBorrower
                                            )
                                        )}
                                </div>
                            </div>

                            {!isAllDetailsMode && (
                                <>
                                    {/* Current Employment Details Section */}
                                    <div className="mb-6">
                                        <div className="flex items-center bg-red-500 text-white p-2 mb-4">
                                            <h3 className="text-base font-medium">
                                                Current Employment Details
                                            </h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {createformFields
                                                .filter(
                                                    (field) =>
                                                        field.section ===
                                                        "Current Employment Details"
                                                )
                                                .map((item) =>
                                                    renderFormField(
                                                        item,
                                                        selectedBorrower
                                                    )
                                                )}
                                        </div>
                                    </div>

                                    {/* Previous Employment Status Section */}
                                    <div className="mb-6">
                                        <div className="flex items-center bg-red-500 text-white p-2 mb-4">
                                            <h3 className="text-base font-medium">
                                                Previous Employment Status
                                            </h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {createformFields
                                                .filter(
                                                    (field) =>
                                                        field.section ===
                                                        "Previous Employment Status"
                                                )
                                                .map((item) =>
                                                    renderFormField(
                                                        item,
                                                        selectedBorrower
                                                    )
                                                )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Emergency Contact Section */}
                            <div className="mb-6">
                                <div className="flex items-center bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Emergency Contact
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section ===
                                                "Emergency Contact"
                                        )
                                        .map((item) =>
                                            renderFormField(
                                                item,
                                                selectedBorrower
                                            )
                                        )}
                                </div>
                            </div>

                            {/* Lawyer Firm Section */}
                            <div className="mb-6">
                                <div className="flex items-center bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Lawyer Firm
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section === "Lawyer Firm"
                                        )
                                        .map((item) => renderFormField(item))}
                                </div>
                            </div>

                            {/* Remarks Section */}
                            <div className="mb-6">
                                <div className="flex items-center bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Remarks
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section === "Remarks"
                                        )
                                        .map((item) => renderFormField(item))}
                                </div>
                            </div>

                            {/* Footer Buttons */}
                            <div className="flex flex-col md:flex-row justify-between mt-8 space-y-4 md:space-y-0">
                                <button
                                    type="button"
                                    onClick={() => history.push("/cases")}
                                    className="px-8 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={user.auth.type === "admin"}
                                    className={`px-8 py-2 ${
                                        user.auth.type === "admin"
                                            ? "bg-gray-400 cursor-not-allowed"
                                            : "bg-black hover:bg-blue-400 hover:text-black"
                                    } text-white rounded-lg`}
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {activeTab === "caseLogs" && (
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <div className="text-2xl font-bold text-lynch">
                                Case Logs
                            </div>
                        </div>

                        {caseData?.log &&
                        caseData.log.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border-collapse">
                                    <thead>
                                        <tr className="bg-blue-50">
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                                #
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                DateTime
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Type
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Remarks
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Action By
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {caseData.log.map(
                                            (log, index) => (
                                                <tr
                                                    key={index}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                                                        {index + 1}
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {log.createdAt
                                                                ? moment(
                                                                      parseInt(
                                                                          log.createdAt
                                                                      )
                                                                  ).format(
                                                                      "DD-MM-YYYY HH:mm"
                                                                  )
                                                                : "—"}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {log.type ===
                                                            "updatedbankstatus"
                                                                ? "Updated Bank Status"
                                                                : log.type ===
                                                                  "updatecasestatus"
                                                                ? "Update Case Status"
                                                                : log.type ===
                                                                  "addDocument"
                                                                ? "Add Document"
                                                                : log.type ===
                                                                  "addBank"
                                                                ? "Add Bank"
                                                                : log.type ===
                                                                  "updateCase"
                                                                ? "Update Case"
                                                                : log.type ===
                                                                  "createCase"
                                                                ? "Create Case"
                                                                : log.type ===
                                                                  "others"
                                                                ? "Others"
                                                                : log.type ||
                                                                  "—"}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div className="text-sm text-gray-900 max-w-md">
                                                            {log.remarks ? (
                                                                <div className="line-clamp-2 hover:line-clamp-none cursor-pointer transition-all duration-200">
                                                                    {
                                                                        log.remarks
                                                                    }
                                                                </div>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {log.createdBy?.name
                                                                ? capitalizeWords(
                                                                      log
                                                                          .createdBy
                                                                          .name
                                                                  )
                                                                : "—"}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-gray-500 italic p-4 bg-gray-50 rounded-md border border-gray-200 text-center">
                                No logs available
                            </div>
                        )}
                    </div>
                )}

                {activeTab === "banks" && (
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <div className="text-2xl font-bold text-gray-800">
                                Banks
                            </div>
                            <div className="table-header-actions">
                                <button
                                    className="flex items-center text-white bg-black py-2 px-4 rounded-lg shadow-sm hover:bg-blue-400 hover:text-black focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                                    onClick={() => {
                                        setAddBankModalState(true);
                                    }}
                                    disabled={user.auth.type === "admin"}
                                >
                                    <span className="mr-1">+</span> Add Bank
                                </button>
                            </div>
                        </div>

                        <div className="bg-blue-50 rounded-lg shadow-sm mb-4">
                            <div className="bg-orange-400 text-white py-3 px-4 rounded-t-lg">
                                <h3 className="text-base font-medium">
                                    Bank Details
                                </h3>
                            </div>

                            {banks && banks.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full bg-white border-collapse">
                                        <thead>
                                            <tr className="bg-gray-100">
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    #
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Bank Name
                                                </th>

                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Banker Name
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Bank Contact
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Bank Email
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Last Updated
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Created At
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {banks.map((bank, index) => (
                                                <tr
                                                    key={index}
                                                    className="hover:bg-gray-50 transition-colors duration-150"
                                                >
                                                    <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                                                        {index + 1}
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {bank.bankName}
                                                        </div>
                                                    </td>

                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {bank.bankerName}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {bank.bankContact}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {bank.bankEmail}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {bank.updatedAt
                                                                ? moment(
                                                                      parseInt(
                                                                          bank.updatedAt
                                                                      )
                                                                  ).format(
                                                                      "DD-MM-YYYY HH:mm"
                                                                  )
                                                                : "—"}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                            {bank.status ===
                                                            "underbankprocessing"
                                                                ? "Under Bank Processing"
                                                                : bank.status ===
                                                                  "draft"
                                                                ? "Draft"
                                                                : bank.status ===
                                                                  "approved"
                                                                ? "Approved"
                                                                : bank.status ===
                                                                  "rejected"
                                                                ? "Rejected"
                                                                : bank.status}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {bank.createdAt
                                                                ? moment(
                                                                      parseInt(
                                                                          bank.createdAt
                                                                      )
                                                                  ).format(
                                                                      "DD-MM-YYYY HH:mm"
                                                                  )
                                                                : "—"}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                className="px-3 py-1 bg-black text-white rounded-md hover:bg-blue-400 hover:text-black transition-colors flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                                                onClick={() => {
                                                                    setCaseBankModalState(
                                                                        true
                                                                    );
                                                                    setSelectedBankId(
                                                                        bank._id
                                                                    );
                                                                }}
                                                            >
                                                                <FiEdit className="w-4 h-4 mr-1" />
                                                                <span className="text-xs">
                                                                    Update
                                                                </span>
                                                            </button>
                                                            {user.auth.type !== "manager" && (
                                                                <button
                                                                    className="px-3 py-1 bg-black text-white rounded-md hover:bg-blue-400 hover:text-black transition-colors flex items-center focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                                                    onClick={() =>
                                                                        handleDeleteBank(
                                                                            bank._id
                                                                        )
                                                                    }
                                                                >
                                                                    <FiTrash2 className="w-4 h-4 mr-1" />
                                                                    <span className="text-xs">
                                                                        Delete
                                                                    </span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-gray-500 italic p-6 bg-white rounded-b-lg text-center">
                                    No banks have been added yet. Click "Add
                                    Bank" to add one.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "documents" && (
                    <>
                        <div className="p-6">
                            <div className="text-2xl font-bold text-lynch mb-6">
                                Update Case Status
                            </div>

                            <div className="bg-blue-50 rounded-lg shadow-sm mb-4">
                                <div className="bg-orange-400 text-white py-3 px-4 rounded-t-lg">
                                    <h3 className="text-base font-medium">
                                        Current Status
                                    </h3>
                                </div>
                                <div className="p-4">
                                    <div className="text-lg font-medium text-gray-800">
                                        Current Status:{" "}
                                        <span
                                            className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                                caseData?.status === "loanApproved"
                                                    ? "bg-green-500 text-white"
                                                    : caseData?.status === "accepted"
                                                    ? "bg-green-500 text-white"
                                                    : caseData?.status === "cancelled"
                                                    ? "bg-red-500 text-white"
                                                    : caseData?.status === "rejected"
                                                    ? "bg-purple-500 text-white"
                                                    : caseData?.status === "submitted"
                                                    ? "bg-orange-500 text-white"
                                                    : caseData?.status === "draft"
                                                    ? "bg-yellow-100 text-yellow-800"
                                                    : "bg-gray-100 text-gray-800"
                                            }`}
                                        >
                                            {caseData?.status
                                                ? statusOptions.find(
                                                      (opt) =>
                                                          opt.value ===
                                                                                                            caseData.status
                                              )?.label ||
                                              caseData.status
                                                  .charAt(0)
                                                  .toUpperCase() +
                                                  caseData.status.slice(
                                                      1
                                                  )
                                                : "Unknown"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <form
                                onSubmit={handleSubmitStatusForm(
                                    handleUpdateCaseStatus
                                )}
                                className="max-w-md"
                            >
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        New Status
                                    </label>
                                    <Controller
                                        as={
                                            <Select
                                                styles={customStyles}
                                                isSearchable={false}
                                                placeholder="Select new status"
                                            />
                                        }
                                        name="status"
                                        control={statusFormControl}
                                        rules={{ required: true }}
                                        options={statusOptions}
                                    />
                                    {statusErrors.status && (
                                        <p className="text-red-400 text-xs mt-1">
                                            Status cannot be empty
                                        </p>
                                    )}
                                </div>

                                {user.auth.type !== "admin" && (
                                    <div className="flex space-x-4">
                                        <button
                                            type="submit"
                                            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-blue-400 hover:text-black focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            Update Status
                                        </button>
                                    </div>
                                )}
                            </form>
                        </div>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <div className="text-2xl font-bold text-lynch">
                                    Documents
                                </div>
                                <div className="table-header-actions">
                                    <div
                                        className="text-medium border-solid cursor-pointer border border-gray-400 bg-white text-lynch text-sm p-2 text-center hover:bg-iconpink hover:text-white"
                                        onClick={() => {
                                            setAddDocumentModalState(true);
                                        }}
                                    >
                                        <IoIosAdd className="inline" /> Add
                                        Documents
                                    </div>
                                </div>
                            </div>

                            {/* Group documents by type */}
                            {documents && documents.length > 0 ? (
                                <div className="bg-blue-50 rounded-md shadow-sm">
                                    <div className="bg-orange-400 text-white py-2 px-4 rounded-t-md">
                                        <h3 className="text-base font-medium">
                                            Case Documents
                                        </h3>
                                    </div>
                                    {/* Desktop header - hidden on mobile */}
                                    <div className="hidden md:grid grid-cols-12 px-4 py-3 bg-gray-100 border-b border-gray-300">
                                        <div className="col-span-3 font-medium text-gray-600 text-sm">
                                            Category
                                        </div>
                                        <div className="col-span-9 font-medium text-gray-600 text-sm">
                                            Documents
                                        </div>
                                    </div>
                                    {Object.entries(
                                        documents.reduce((acc, doc) => {
                                            // Convert document type to uppercase for consistent grouping
                                            const docType = doc.type;
                                            if (!acc[docType]) {
                                                acc[docType] = [];
                                            }
                                            acc[docType].push(doc);
                                            return acc;
                                        }, {})
                                    ).map(([category, docs]) => (
                                        <div
                                            key={category}
                                            className="mb-1 border-b border-gray-200 last:border-b-0"
                                        >
                                            {/* Mobile Layout */}
                                            <div className="block md:hidden bg-white">
                                                <div className="bg-blue-100 px-4 py-3 border-b border-gray-200">
                                                    <div className="font-semibold text-blue-800 text-sm">
                                                        {category.toUpperCase()}
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    {docs.map((doc, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="bg-gray-50 rounded-lg p-3 mb-3 last:mb-0"
                                                        >
                                                            <div className="mb-3">
                                                                <div className="font-medium text-gray-800 text-sm mb-2">
                                                                    {doc.name
                                                                        ? `${
                                                                              doc.name
                                                                          }${
                                                                              doc.bankName &&
                                                                              doc.bankName !==
                                                                                  ""
                                                                                  ? ` - ${doc.bankName}`
                                                                                  : ""
                                                                          }`
                                                                        : doc.type}
                                                                </div>
                                                                <div className="text-gray-500 text-xs flex flex-wrap items-center gap-1">
                                                                    <span className="flex items-center">
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-3 w-3 mr-1"
                                                                            fill="none"
                                                                            viewBox="0 0 24 24"
                                                                            stroke="currentColor"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z"
                                                                            />
                                                                        </svg>
                                                                        {moment(
                                                                            parseInt(
                                                                                doc.createdAt
                                                                            )
                                                                        ).format(
                                                                            "DD-MM-YYYY"
                                                                        )}
                                                                    </span>
                                                                    {doc.createdBy && (
                                                                        <span className="text-black ml-1">
                                                                            by{" "}
                                                                            {
                                                                                doc
                                                                                    .createdBy
                                                                                    .name
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex space-x-2">
                                                                <a
                                                                    href={doc.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="flex-1"
                                                                >
                                                                    <button className="w-full py-2 px-3 bg-black text-white rounded hover:bg-blue-400 hover:text-black transition-colors flex items-center justify-center">
                                                                        <FiEye className="h-4 w-4" />
                                                                        <span className="ml-2 text-sm">
                                                                            View
                                                                        </span>
                                                                    </button>
                                                                </a>
                                                                {user.auth.type !== "manager" && (
                                                                    <button
                                                                        onClick={() =>
                                                                            handleDeleteDocument(
                                                                                doc._id
                                                                            )
                                                                        }
                                                                        className="flex-1 py-2 px-3 bg-black text-white rounded hover:bg-blue-400 hover:text-black transition-colors flex items-center justify-center"
                                                                    >
                                                                        <FiTrash2 className="h-4 w-4" />
                                                                        <span className="ml-2 text-sm">
                                                                            Delete
                                                                        </span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Desktop Layout */}
                                            <div className="hidden md:grid grid-cols-12 bg-white">
                                                <div className="col-span-3 bg-blue-100 flex items-center px-4 py-3">
                                                    <div className="font-semibold text-blue-800">
                                                        {category ===
                                                        "originalbankstatement" ? (
                                                            <>
                                                                ORIGINAL
                                                                <br />
                                                                BANK
                                                                <br />
                                                                STATEMENT
                                                            </>
                                                        ) : category ===
                                                          "bankapplicationform" ? (
                                                            <>
                                                                BANK
                                                                <br />
                                                                APPLICATION
                                                                <br />
                                                                FORM
                                                            </>
                                                        ) : (
                                                            category.toUpperCase()
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-span-9 p-2">
                                                    {docs.map((doc, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="grid grid-cols-12 items-center py-2 px-2 rounded-md hover:bg-gray-50 mb-1"
                                                        >
                                                            <div className="col-span-8">
                                                                <div className="font-medium text-gray-800">
                                                                    {doc.name
                                                                        ? `${
                                                                              doc.name
                                                                          }${
                                                                              doc.bankName &&
                                                                              doc.bankName !==
                                                                                  ""
                                                                                  ? ` - ${doc.bankName}`
                                                                                  : ""
                                                                          }`
                                                                        : doc.type}
                                                                </div>
                                                                <div className="text-gray-500 text-xs flex items-center mt-1">
                                                                    <span className="mr-1">
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-4 w-4"
                                                                            fill="none"
                                                                            viewBox="0 0 24 24"
                                                                            stroke="currentColor"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                    {moment(
                                                                        parseInt(
                                                                            doc.createdAt
                                                                        )
                                                                    ).format(
                                                                        "YYYY-MM-DD hh:mm A"
                                                                    )}
                                                                    {doc.createdBy && (
                                                                        <span className="ml-2 text-black">
                                                                            upload
                                                                            by{" "}
                                                                            {
                                                                                doc
                                                                                    .createdBy
                                                                                    .name
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="col-span-4 flex justify-end space-x-2">
                                                                <a
                                                                    href={
                                                                        doc.url
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <button className="py-2 px-3 bg-black text-white rounded hover:bg-blue-400 hover:text-black transition-colors flex items-center">
                                                                        <FiEye className="h-4 w-4" />
                                                                        <span className="ml-1 text-xs">
                                                                            View
                                                                        </span>
                                                                    </button>
                                                                </a>
                                                                {user.auth.type !== "manager" && (
                                                                    <button
                                                                        onClick={() =>
                                                                            handleDeleteDocument(
                                                                                doc._id
                                                                            )
                                                                        }
                                                                        className="py-2 px-3 bg-black text-white rounded hover:bg-blue-400 hover:text-black transition-colors flex items-center"
                                                                    >
                                                                        <FiTrash2 className="h-4 w-4" />
                                                                        <span className="ml-1 text-xs">
                                                                            Delete
                                                                        </span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-gray-500 italic p-4 bg-gray-50 rounded-md border border-gray-200 text-center">
                                    No documents available
                                </div>
                            )}
                        </div>
                    </>
                )}

                {activeTab === "terms" && (
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <div
                                className="text-2xl font-bold text-lynch"
                                
                            >
                                Terms
                            </div>
                            {(caseData?.status ===
                                "loanApproved" || caseData?.status === "accepted") &&
                                caseData?.document?.some(
                                    (doc) => doc.type === "e-application"
                                ) &&
                                caseData?.document?.some(
                                    (doc) => doc.type === "signedlo"
                                ) && (
                                    <div className="table-header-actions">
                                        <button
                                            className="flex items-center text-white bg-blue-400 py-2 px-4 rounded-lg shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                                            onClick={() => {
                                                setAddTermModalState(true);
                                            }}
                                        >
                                            <span className="mr-1">+</span> Add
                                            Term
                                        </button>
                                    </div>
                                )}
                        </div>

                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="overflow-x-auto md:overflow-x-visible">
                                <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Borrower
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Policy Number
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Plan
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Payment Method
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Payment Term (Years)
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Inforce Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {terms && terms.length > 0 ? (
                                        terms.map(
                                            (term) => (
                                                <tr
                                                    key={term._id}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td className="px-6 py-4">
                                                        {
                                                            caseData.borrower.find(
                                                                (b) =>
                                                                    b._id ===
                                                                    term.borrower
                                                            )?.name
                                                        }
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {term.policyNumber}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {term.plan === "ul"
                                                            ? "Unit Linked"
                                                            : term.plan === "tl"
                                                            ? "Traditional"
                                                            : term.plan}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        RM{" "}
                                                        {term.amount.toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {term.method ===
                                                        "monthly"
                                                            ? "Monthly"
                                                            : term.method ===
                                                              "quarterly"
                                                            ? "Quarterly"
                                                            : term.method ===
                                                              "halfyearly"
                                                            ? "Half Yearly"
                                                            : term.method ===
                                                              "yearly"
                                                            ? "Yearly"
                                                            : term.method}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {term.term}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {moment(
                                                            Number(
                                                                term.inforceDate
                                                            ) ||
                                                                term.inforceDate
                                                        ).format("DD-MM-YYYY")}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span
                                                            className={
                                                                term.status ===
                                                                "active"
                                                                    ? "text-green-600"
                                                                    : "text-red-600"
                                                            }
                                                        >
                                                            {term.status
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                                term.status.slice(
                                                                    1
                                                                )}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => {
                                                                    setTermModalData(
                                                                        term
                                                                    );
                                                                    setEditTermModalState(
                                                                        true
                                                                    );
                                                                }}
                                                                className="p-2 text-orange-400 hover:text-orange-600"
                                                            >
                                                                <FiEdit />
                                                            </button>
                                                            {!term.inforceDate && user.auth.type !== "manager" && (
                                                                <button
                                                                    onClick={() =>
                                                                        handleDeleteTerm(
                                                                            term._id
                                                                        )
                                                                    }
                                                                    className="p-2 text-orange-400 hover:text-orange-600"
                                                                >
                                                                    <FiTrash2 />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan="7"
                                                className="px-6 py-4 text-center text-gray-500 italic"
                                            >
                                                No terms available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "mailHistory" && (
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <div className="text-2xl font-bold text-lynch">
                                Mail History
                            </div>
                            {caseData?.status !== "draft" && (
                                <div className="table-header-actions">
                                    <button
                                        className="flex items-center text-white bg-black py-2 px-4 rounded-md hover:bg-blue-400 hover:text-black"
                                        onClick={() => {
                                            setMailerState(true);
                                        }}
                                    >
                                        <span className="mr-1">+</span> Send New
                                        Email
                                    </button>
                                </div>
                            )}
                        </div>

                        {mailHistory && mailHistory.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border-collapse">
                                    <thead>
                                        <tr className="bg-blue-50">
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                                #
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Receiver
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Content
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {mailHistory.map((mail, index) => (
                                            <tr
                                                key={index}
                                                className="hover:bg-gray-50"
                                            >
                                                <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                                                    {index + 1}
                                                </td>
                                                <td className="py-3 px-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {mail.createdAt
                                                            ? moment(
                                                                  parseInt(
                                                                      mail.createdAt
                                                                  )
                                                              ).format(
                                                                  "DD-MM-YYYY HH:mm"
                                                              )
                                                            : "—"}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {mail.receiver || "—"}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="text-sm text-gray-900 max-w-md">
                                                        {mail.messageContent ? (
                                                            <div className="line-clamp-2 hover:line-clamp-none cursor-pointer transition-all duration-200">
                                                                {
                                                                    mail.messageContent
                                                                }
                                                            </div>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {mail.status || "—"}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-gray-500 italic p-4 bg-gray-50 rounded-md border border-gray-200 text-center">
                                No mail history available
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add the modal component at the end of your return statement, before the closing tags */}
            {createNoteForm && (
                <Modal reset={resetCreateNoteForm}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-4xl w-full">
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-2xl font-bold">
                                    Auto Fill Form
                                </div>
                                <button
                                    onClick={() =>
                                        setShowTemplate(!showTemplate)
                                    }
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                    {showTemplate
                                        ? "Show Form"
                                        : "Show Template"}
                                </button>
                            </div>

                            {!showTemplate ? (
                                <div className="w-full">
                                    <textarea
                                        value={noteContent}
                                        onChange={(e) =>
                                            setNoteContent(e.target.value)
                                        }
                                        className="w-full h-half p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Paste your content here..."
                                    />
                                    <div className="flex justify-end mt-4">
                                        <button
                                            type="button"
                                            onClick={handleSubmitCreateNoteForm}
                                            className="px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-600"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full">
                                    <div className="mb-2 font-medium">
                                        Template Reference
                                    </div>
                                    <button
                                        onClick={() =>
                                            navigator.clipboard
                                                .writeText(`Sales Type: Project
Apply For: Conventional
Flexi Type: Semi flexi
Unit Number: DEMO-C
Full Property Address: C3-3-13A, Solaris Dutamas, No.1, Jalan Dutamas 1,
Renovation Details: Kitchen Cabinet, Kitchen Tabletop, Jacuzzi
Project Name: 163 MK
Property Type: Apartment
Completed: Under Con
Bumi/Non-Bumi: Non Bumi
Freehold/Leasehold: Freehold
Land Size: 3,000 sqft
Build Up: 1,300 sqft
SPA Price(RM): 1,000,000
Margin Requested: 90%
Loan Amount(RM): 900,000
Loan Tenure: 35
Remarks: Good client speed up
Property Agent: Mr Loi
SPA Name(Borrower): John Doe
SPA Lawyer Firm: Mr Singh
LA Lawyer Firm: Alucard
Valuation Firm: Law Firm
Valuer Name: Kin Kooi Chin
Market Value(RM): 1,200,000`)
                                        }
                                        className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 w-full"
                                    >
                                        Copy Template
                                    </button>
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <pre className="whitespace-pre-wrap text-sm text-gray-600">
                                            {`Sales Type: Project
Apply For: Conventional
Flexi Type: Semi flexi
Unit Number: DEMO-C
Full Property Address: C3-3-13A, Solaris Dutamas, No.1, Jalan Dutamas 1,
Renovation Details: Kitchen Cabinet, Kitchen Tabletop, Jacuzzi
Project Name: 163 MK
Property Type: Apartment
Completed: Under Con
Bumi/Non-Bumi: Non Bumi
Freehold/Leasehold: Freehold
Land Size: 3,000 sqft
Build Up: 1,300 sqft
SPA Price(RM): 1,000,000
Margin Requested: 90%
Loan Amount(RM): 900,000
Loan Tenure: 35
Remarks: Good client speed up
Property Agent: Mr Loi
SPA Name(Borrower): John Doe
SPA Lawyer Firm: Mr Singh
LA Lawyer Firm: Alucard
Valuation Firm: Law Firm
Valuer Name: Kin Kooi Chin
Market Value(RM): 1,200,000`}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add this modal component near the end of the return statement, before the closing tags */}
            {borrowerNoteForm && (
                <Modal reset={resetBorrowerNoteForm}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-4xl w-full">
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-2xl font-bold">
                                    Auto Fill Borrower Form
                                </div>
                                <button
                                    onClick={() =>
                                        setShowTemplate(!showTemplate)
                                    }
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                    {showTemplate
                                        ? "Show Form"
                                        : "Show Template"}
                                </button>
                            </div>

                            {!showTemplate ? (
                                <div className="w-full">
                                    <textarea
                                        value={borrowerNoteContent}
                                        onChange={(e) =>
                                            setBorrowerNoteContent(
                                                e.target.value
                                            )
                                        }
                                        className="w-full h-half p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Paste your content here..."
                                    />
                                    <div className="flex justify-end mt-4">
                                        <button
                                            type="button"
                                            onClick={
                                                handleSubmitBorrowerNoteForm
                                            }
                                            className="px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-600"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full">
                                    <div className="mb-2 font-medium">
                                        Template Reference
                                    </div>
                                    <button
                                        onClick={() =>
                                            navigator.clipboard
                                                .writeText(`NAME : John Doe
NRIC : S1234567A
CONTACT : +65 9123 4567
EMAIL ADDRESS : johndoe@example.com
EDUCATION LEVEL: Bachelor's Degree in Business Administration
SALARY: 5000
SMOKING/NO: No
MOTHER NAME: Mary Doe
MAILING ADDRESS : 123 Orchard Road, #12-34, Singapore 238567
LENGTH OF STAY In this MAILING ADDRESS: 8 years

CURRENT COMPANY NAME: ABC Solutions Pte Ltd
CURRENT COMPANY ADDRESS : 456 Clementi Road, Singapore 129567
CURRENT COMPANY TELEPHONE : +65 6123 4567
CURRENT POSITION: IT Analyst
CURRENT DATE OF JOINING: 06 June 2020
CURRENT NATURE OF BUSINESS : IT Consulting

PREVIOUS COMPANY NAME : XYZ Tech Pte Ltd
PREVIOUS ADDRESS : 789 Raffles Place, Singapore 048624
PREVIOUS HR DIRECT LINE : +65 6321 8765
PREVIOUS YRS IN SERVICE : 5 years
PREVIOUS POSITION : Project Manager
PREVIOUS NATURE OF BUSINESS : Technology Solutions

MARITAL STATUS: divorced
SPOUSE NAME : Jane Doe
SPOUSE IC : 204032104203
SPOUSE CONTACT : 012331231
SPOUSE OCCUPATION : Accountant
SPOUSE COMPANY NAME: ABC Solutions Pte Ltd
SPOUSE MONTHLY INCOME: 10000
NO. CHILDREN: 2

FULL NAME: Michael Doe
IC NUMBER: 123456789012
EMERGENCY CONTACT: +65 9123 6789
RELATIONSHIP: Brother
HOUSE ADDRESS: 890 Tanjong Pagar Road, #03-21, Singapore 089617
OCCUPATION: Software Engineer`)
                                        }
                                        className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 w-full"
                                    >
                                        Copy Template
                                    </button>
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <pre className="whitespace-pre-wrap text-sm text-gray-600">
                                            {`NAME : John Doe
NRIC : S1234567A
SALARY: 5000
CONTACT : +65 9123 4567
EMAIL ADDRESS : johndoe@example.com
EDUCATION LEVEL: Bachelor's Degree in Business Administration
SMOKING/NO: No
MOTHER NAME: Mary Doe
MAILING ADDRESS : 123 Orchard Road, #12-34, Singapore 238567
LENGTH OF STAY In this MAILING ADDRESS: 8 years

CURRENT COMPANY NAME: ABC Solutions Pte Ltd
CURRENT COMPANY ADDRESS : 456 Clementi Road, Singapore 129567
CURRENT COMPANY TELEPHONE : +65 6123 4567
CURRENT POSITION: IT Analyst
CURRENT DATE OF JOINING: 06 June 2020
CURRENT NATURE OF BUSINESS : IT Consulting

PREVIOUS COMPANY NAME : XYZ Tech Pte Ltd
PREVIOUS ADDRESS : 789 Raffles Place, Singapore 048624
PREVIOUS HR DIRECT LINE : +65 6321 8765
PREVIOUS YRS IN SERVICE : 5 years
PREVIOUS POSITION : Project Manager
PREVIOUS NATURE OF BUSINESS : Technology Solutions

MARITAL STATUS: divorced
SPOUSE NAME : Jane Doe
SPOUSE IC : 204032104203
SPOUSE CONTACT : 012331231
SPOUSE OCCUPATION : Accountant
SPOUSE COMPANY NAME: ABC Solutions Pte Ltd
SPOUSE MONTHLY INCOME: 10000
NO. CHILDREN: 2

FULL NAME: Michael Doe
IC NUMBER: 123456789012
EMERGENCY CONTACT: +65 9123 6789
RELATIONSHIP: Brother
HOUSE ADDRESS: 890 Tanjong Pagar Road, #03-21, Singapore 089617
OCCUPATION: Software Engineer
`}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Document Modal */}
            {addDocumentModalState && (
                <Modal reset={resetAddDocumentModal}>
                    {isUploadingDocument ? (
                        <div className="container h-full mx-auto flex flex-col justify-center items-center p-4 h-64">
                            <div className="animate-spin w-16 h-32 border-4 border-blue-400 border-t-transparent rounded-full mb-4"></div>
                            <p className="text-lg font-medium text-gray-700">Uploading documents...</p>
                            <p className="text-sm text-gray-500">Please wait while your files are being processed</p>
                        </div>
                    ) : (
                        <div className="container h-full mx-auto flex justify-center items-center">
                            <div className="panel bg-white p-6 rounded-md max-w-lg w-full">
                                <div className="mb-4 text-2xl font-bold">
                                    Add Document
                                </div>

                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();

                                        // Prevent submission if already uploading
                                        if (isUploadingDocument) {
                                            return;
                                        }

                                        const formData = new FormData(e.target);
                                        const files = e.target.file.files;
                                        const type = formData.get("type");
                                        const documentNameInput = (
                                            formData.get("name") || ""
                                        )
                                            .toString()
                                            .trim();
                                        const bankName = formData.get("bankName");

                                        if (files.length > 0 && type) {
                                            try {
                                                const filesArray = Array.from(files);
                                                for (let i = 0; i < filesArray.length; i++) {
                                                    const file = filesArray[i];
                                                    // Check for Chinese characters in filename
                                                    if (/[\u4E00-\u9FFF]/.test(file.name)) {
                                                        alert("Files with Chinese characters in the name are not allowed");
                                                        return;
                                                    }

                                                    const fileName = documentNameInput
                                                        ? i === 0
                                                            ? documentNameInput
                                                            : `${documentNameInput}-${i + 1}`
                                                        : file.name;
                                                    await handleDocumentUpload(
                                                        file,
                                                        type,
                                                        fileName,
                                                        bankName
                                                    );
                                                }
                                                alert("All documents uploaded successfully");
                                                setAddDocumentModalState(false);
                                            } catch (error) {
                                                alert("Failed to upload one or more documents");
                                            }
                                        } else {
                                            alert(
                                                "Please select file(s) and document type"
                                            );
                                        }
                                    }}
                                >
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Document Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            disabled={isUploadingDocument}
                                            className={`w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isUploadingDocument
                                                    ? "bg-gray-100 cursor-not-allowed"
                                                    : ""
                                            }`}
                                            placeholder="Enter document name"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Document Type
                                        </label>
                                        <select
                                            name="type"
                                            required
                                            disabled={isUploadingDocument}
                                            onChange={(e) => {
                                                const bankField =
                                                    document.getElementById(
                                                        "bankField"
                                                    );
                                                if (
                                                    e.target.value ===
                                                    "bankapplicationform"
                                                ) {
                                                    bankField.style.display =
                                                        "block";
                                                } else {
                                                    bankField.style.display =
                                                        "none";
                                                }
                                            }}
                                            className={`w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isUploadingDocument
                                                    ? "bg-gray-100 cursor-not-allowed"
                                                    : ""
                                            }`}
                                        >
                                            <option value="">
                                                Select document type
                                            </option>
                                            <option value="identity">
                                                Identity
                                            </option>
                                            <option value="income">Income</option>
                                            <option value="salesform">
                                                Sales Form
                                            </option>
                                            <option value="ccris">CCRIS</option>
                                            <option value="payslip">Payslip</option>

                                            <option value="originalbankstatement">
                                                Original Bank Statement
                                            </option>

                                            <option value="others">Others</option>

                                            <option value="bankapplicationform">
                                                Bank Application Form
                                            </option>
                                            <option value="signedlo">
                                                Signed LO
                                            </option>
                                            <option value="e-application">
                                                E-Application Form
                                            </option>
                                        </select>
                                    </div>

                                    <div
                                        id="bankField"
                                        style={{ display: "none" }}
                                        className="mb-4"
                                    >
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Bank Name
                                        </label>
                                        <select
                                            name="bankName"
                                            disabled={isUploadingDocument}
                                            className={`w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isUploadingDocument
                                                    ? "bg-gray-100 cursor-not-allowed"
                                                    : ""
                                            }`}
                                        >
                                            <option value="">Select bank</option>
                                            {caseData.bank.map(
                                                (bank) => (
                                                    <option
                                                        key={bank._id}
                                                        value={bank.bankName}
                                                    >
                                                        {bank.bankName}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>

                                    <div className="mb-4">
                                        <p className="text-sm text-gray-600">
                                            Maximum File Size : 5MB.
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            Click{" "}
                                            <a
                                                href="http://ilovepdf.com/compress_pdf"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-500 hover:text-blue-700 underline"
                                            >
                                                HERE
                                            </a>{" "}
                                            to compress your document before upload.
                                        </p>
                                    </div>

                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select File
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="file"
                                                name="file"
                                                required
                                                multiple
                                                disabled={isUploadingDocument}
                                                className={`absolute inset-0 w-full h-full opacity-0 z-10 ${
                                                    isUploadingDocument
                                                        ? "cursor-not-allowed"
                                                        : "cursor-pointer"
                                                }`}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                onChange={(e) => {
                                                    const files = Array.from(
                                                        e.target.files
                                                    );
                                                    const MAX_FILE_SIZE =
                                                        5 * 1024 * 1024; // 5MB in bytes

                                                    // Check each file size
                                                    const oversizedFiles =
                                                        files.filter(
                                                            (file) =>
                                                                file.size >
                                                                MAX_FILE_SIZE
                                                        );
                                                    if (oversizedFiles.length > 0) {
                                                        alert(
                                                            `The following files exceed the 5MB limit:\n${oversizedFiles
                                                                .map((f) => f.name)
                                                                .join("\n")}`
                                                        );
                                                        e.target.value = ""; // Clear the file input
                                                        document.getElementById(
                                                            "file-name-display"
                                                        ).textContent =
                                                            "No files selected";
                                                        return;
                                                    }

                                                    const fileNames = files
                                                        .map((file) => file.name)
                                                        .join(", ");
                                                    document.getElementById(
                                                        "file-name-display"
                                                    ).textContent =
                                                        fileNames ||
                                                        "No files selected";
                                                }}
                                            />
                                            <div
                                                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                                                    isUploadingDocument
                                                        ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                                                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer"
                                                }`}
                                            >
                                                <div className="flex flex-col items-center">
                                                    <svg
                                                        className="w-12 h-12 text-gray-400 mb-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                                        />
                                                    </svg>
                                                    <p
                                                        className={`text-lg font-medium mb-2 ${
                                                            isUploadingDocument
                                                                ? "text-gray-400"
                                                                : "text-gray-600"
                                                        }`}
                                                    >
                                                        Drop your files here or click to browse
                                                    </p>
                                                    <p
                                                        className={`text-sm mb-4 ${
                                                            isUploadingDocument
                                                                ? "text-gray-400"
                                                                : "text-gray-500"
                                                        }`}
                                                    >
                                                        Supports: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB per file)
                                                    </p>
                                                    <div
                                                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                                            isUploadingDocument
                                                                ? "bg-gray-300 text-gray-500"
                                                                : "bg-orange-400 text-white"
                                                        }`}
                                                    >
                                                        Choose Files
                                                    </div>
                                                    <p
                                                        id="file-name-display"
                                                        className="text-sm text-gray-600 mt-2 font-medium"
                                                    >
                                                        No files selected
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            onClick={resetAddDocumentModal}
                                            disabled={isUploadingDocument}
                                            className={`px-4 py-2 rounded-lg ${
                                                isUploadingDocument
                                                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                    : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                                            }`}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isUploadingDocument}
                                            className={`px-4 py-2 rounded-lg ${
                                                isUploadingDocument
                                                    ? "bg-gray-400 text-white cursor-not-allowed"
                                                    : "bg-blue-400 text-white hover:bg-blue-600"
                                            }`}
                                        >
                                            Upload
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {/* Add Bank Modal */}
            {addBankModalState && (
                <Modal reset={resetAddBankModal}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-md w-full">
                            <div className="mb-4 text-2xl font-bold">
                                Add Bank
                            </div>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    const formData = new FormData(e.target);
                                    const bankSubEmails = addBankSubEmails.filter(email => email && email.trim() !== "");
                                    const bankData = {
                                        bankName: formData.get("bankName"),
                                        status:
                                            formData.get("status") || "draft",
                                        bankContact:
                                            formData.get("bankContact"),
                                        bankerName: formData.get("bankerName"),
                                        bankEmail: formData.get("bankEmail"),
                                        bankSubEmail: bankSubEmails,
                                    };

                                    if (bankData.bankName) {
                                        handleAddBank(bankData);
                                    } else {
                                        alert("Please enter bank name");
                                    }
                                }}
                            >
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bank Name
                                    </label>
                                    <select
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        name="bankName"
                                        required
                                    >
                                        <option value="">Select Bank</option>
                                        <option value="Affin Bank">
                                            Affin Bank
                                        </option>
                                        <option value="AIA">AIA</option>
                                        <option value="Al-Rajhi">
                                            Al-Rajhi
                                        </option>
                                        <option value="Alliance Bank">
                                            Alliance Bank
                                        </option>
                                        <option value="AmBank">AmBank</option>
                                        <option value="Bank of China">
                                            Bank of China
                                        </option>
                                        <option value="BSN">BSN</option>
                                        <option value="CIMB Bank">
                                            CIMB Bank
                                        </option>
                                        <option value="Hong Leong Bank">
                                            Hong Leong Bank
                                        </option>
                                        <option value="HSBC Bank">
                                            HSBC Bank
                                        </option>
                                        <option value="ICBC">ICBC</option>
                                        <option value="Bank Islam">
                                            Bank Islam
                                        </option>
                                        <option value="Kuwait Finance">
                                            Kuwait Finance
                                        </option>
                                        <option value="Maybank">Maybank</option>
                                        <option value="MBSB Bank">
                                            MBSB Bank
                                        </option>
                                        <option value="Bank Muamalat">
                                            Bank Muamalat
                                        </option>
                                        <option value="OCBC Bank">
                                            OCBC Bank
                                        </option>
                                        <option value="Public Bank">
                                            Public Bank
                                        </option>
                                        <option value="Bank Rakyat">
                                            Bank Rakyat
                                        </option>
                                        <option value="RHB Bank">
                                            RHB Bank
                                        </option>
                                        <option value="Standard Chartered Bank">
                                            Standard Chartered Bank
                                        </option>
                                        <option value="UOB Bank">
                                            UOB Bank
                                        </option>
                                        <option value="Others">Others</option>
                                    </select>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Banker Name
                                    </label>
                                    <input
                                        type="text"
                                        name="bankerName"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter banker name"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bank Contact
                                    </label>
                                    <input
                                        type="text"
                                        name="bankContact"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter bank contact name"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bank Email
                                    </label>
                                    <input
                                        type="email"
                                        name="bankEmail"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter bank email address"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bank Sub Emails
                                    </label>
                                    <div>
                                        {addBankSubEmails.map((email, index) => (
                                            <div key={index} className="flex gap-2 mb-2">
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => {
                                                        const newEmails = [...addBankSubEmails];
                                                        newEmails[index] = e.target.value;
                                                        setAddBankSubEmails(newEmails);
                                                    }}
                                                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Enter additional bank email"
                                                />
                                                {index === 0 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setAddBankSubEmails([...addBankSubEmails, ''])}
                                                        className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                                                    >
                                                        Add Email
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newEmails = addBankSubEmails.filter((_, i) => i !== index);
                                                            setAddBankSubEmails(newEmails);
                                                        }}
                                                        className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        onClick={resetAddBankModal}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-600"
                                    >
                                        Add Bank
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Update Bank Modal */}
            {caseBankModalState && (
                <Modal reset={resetCaseBankModal}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-md w-full">
                            <div className="mb-4 text-2xl font-bold">
                                Update Bank
                            </div>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    const formData = new FormData(e.target);
                                    const bankSubEmails = updateBankSubEmails.filter(email => email && email.trim() !== "");
                                    const bankData = {
                                        status: formData.get("status"),
                                        bankContact:
                                            formData.get("bankContact"),
                                        bankerName: formData.get("bankerName"),
                                        bankEmail: formData.get("bankEmail"),
                                        bankSubEmail: bankSubEmails,
                                    };

                                    handleUpdateBank(bankData);
                                }}
                            >
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Banker Name
                                    </label>
                                    <input
                                        type="text"
                                        name="bankerName"
                                        defaultValue={
                                            banks.find(
                                                (bank) =>
                                                    bank._id === selectedBankId
                                            )?.bankerName || ""
                                        }
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter banker name"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bank Contact
                                    </label>
                                    <input
                                        type="text"
                                        name="bankContact"
                                        defaultValue={
                                            banks.find(
                                                (bank) =>
                                                    bank._id === selectedBankId
                                            )?.bankContact || ""
                                        }
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter bank contact"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bank Email
                                    </label>
                                    <input
                                        type="email"
                                        name="bankEmail"
                                        defaultValue={
                                            banks.find(
                                                (bank) =>
                                                    bank._id === selectedBankId
                                            )?.bankEmail || ""
                                        }
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter bank email"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bank Sub Emails
                                    </label>
                                    <div>
                                        {updateBankSubEmails.map((email, index) => (
                                            <div key={index} className="flex gap-2 mb-2">
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => {
                                                        const newEmails = [...updateBankSubEmails];
                                                        newEmails[index] = e.target.value;
                                                        setUpdateBankSubEmails(newEmails);
                                                    }}
                                                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Enter additional bank email"
                                                />
                                                {index === 0 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setUpdateBankSubEmails([...updateBankSubEmails, ''])}
                                                        className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                                                    >
                                                        Add Email
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newEmails = updateBankSubEmails.filter((_, i) => i !== index);
                                                            setUpdateBankSubEmails(newEmails);
                                                        }}
                                                        className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status
                                    </label>
                                    <select
                                        name="status"
                                        defaultValue={
                                            banks.find(
                                                (bank) =>
                                                    bank._id === selectedBankId
                                            )?.status || "draft"
                                        }
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="draft">
                                            Draft (Pending Document)
                                        </option>
                                        <option value="underbankprocessing">
                                            Under Bank Processing
                                        </option>
                                        <option value="kivHq">
                                            KIV from Bank HQ
                                        </option>
                                        <option value="recommendedHq">
                                            Recommend from HQ
                                        </option>
                                        <option value="approved">
                                            Approved
                                        </option>
                                        <option value="rejected">
                                            Rejected
                                        </option>

                                        <option value="cancelled">
                                            Cancelled
                                        </option>
                                        <option value="onhold">On Hold</option>

                                        <option value="upfrontreject">
                                            Up-Front Reject
                                        </option>
                                        <option value="kivBanker">
                                            KIV From Banker
                                        </option>
                                    </select>
                                </div>
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        onClick={resetCaseBankModal}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-600"
                                    >
                                        Update Bank
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Send New Email Modal */}
            {mailerState && (
                <Modal reset={resetMailerModal}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-lg w-full">
                            <div className="mb-4 text-2xl font-bold">
                                Send New Email
                            </div>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    const formData = new FormData(e.target);
                                    const mailData = {
                                        receiver: formData.get("receiver"),
                                        bankId: formData.get("bankId") || null,
                                    };

                                    if (mailData.receiver && mailData.bankId) {
                                        handleSendMail(mailData);
                                    } else if (mailData.bankId && !mailData.receiver) {
                                        handleSendMail({
                                            receiver: formData.get("bankEmail"),
                                            bankId: formData.get("bankId"),
                                        });
                                    } else {
                                        alert("Please enter receiver email address or select bank");
                                    }
                                }}
                            >
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Receiver Email *
                                    </label>
                                    <input
                                        type="string"
                                        name="receiver"
                                        required={false}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter receiver email address"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Bank
                                    </label>
                                    <select
                                        name="bankId"
                                        required={true}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onChange={(e) => {
                                            const selectedBank = banks.find(bank => bank._id === e.target.value);
                                            if (selectedBank) {
                                                e.target.form.bankEmail.value = selectedBank.bankEmail || '';
                                            }
                                        }}
                                    >
                                        <option value="">Select a bank</option>
                                        {banks.map((bank) => (
                                            <option
                                                key={bank._id}
                                                value={bank._id}
                                            >
                                                {bank.bankName}
                                            </option>
                                        ))}
                                    </select>
                                    <input 
                                        type="hidden"
                                        name="bankEmail"
                                    />
                                </div>
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        onClick={resetMailerModal}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-600"
                                    >
                                        Send Email
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Term Modal */}
            {addTermModalState && (
                <Modal reset={resetAddTermModal}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-md w-full">
                            <div className="mb-4 text-2xl font-bold">
                                Add Term
                            </div>
                            <form onSubmit={handleTermSubmit(handleAddTerm)}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Policy Number
                                    </label>
                                    <input
                                        type="text"
                                        name="policyNumber"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        ref={termRegister({ required: true })}
                                    />
                                    {termErrors.policyNumber && (
                                        <p className="text-red-500 text-xs italic">
                                            Policy number is required
                                        </p>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Borrower
                                    </label>
                                    <Controller
                                        as={Select}
                                        name="borrower"
                                        control={termControl}
                                        rules={{ required: true }}
                                        options={caseData.borrower.map(
                                            (borrower) => ({
                                                value: borrower._id,
                                                label: borrower.name,
                                            })
                                        )}
                                        className="w-full"
                                    />
                                    {termErrors.borrower && (
                                        <p className="text-red-500 text-xs italic">
                                            Borrower is required
                                        </p>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Plan Type
                                    </label>
                                    <Controller
                                        as={Select}
                                        name="plan"
                                        control={termControl}
                                        rules={{ required: true }}
                                        options={[
                                            {
                                                value: "ul",
                                                label: "Unit Linked",
                                            },
                                            {
                                                value: "tl",
                                                label: "Traditional",
                                            },
                                        ]}
                                        styles={customStyles}
                                    />
                                    {termErrors.plan && (
                                        <p className="text-red-500 text-xs italic">
                                            Plan type is required
                                        </p>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Amount (Key in Yearly Amount only, select payment method)
                                    </label>
                                    <input
                                        type="string"
                                        name="amount"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        ref={termRegister({ required: true })}
                                    />
                                    {termErrors.amount && (
                                        <p className="text-red-500 text-xs italic">
                                            Amount is required
                                        </p>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Payment Method
                                    </label>
                                    <Controller
                                        as={Select}
                                        name="method"
                                        control={termControl}
                                        rules={{ required: true }}
                                        options={[
                                            {
                                                value: "monthly",
                                                label: "Monthly",
                                            },
                                            {
                                                value: "quarterly",
                                                label: "Quarterly",
                                            },
                                            {
                                                value: "halfyearly",
                                                label: "Half Yearly",
                                            },
                                            {
                                                value: "yearly",
                                                label: "Yearly",
                                            },
                                        ]}
                                        styles={customStyles}
                                    />
                                    {termErrors.method && (
                                        <p className="text-red-500 text-xs italic">
                                            Payment method is required
                                        </p>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Payment Term (Years)
                                    </label>
                                    <input
                                        type="number"
                                        name="term"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        ref={termRegister({ required: true })}
                                    />
                                    {termErrors.term && (
                                        <p className="text-red-500 text-xs italic">
                                            Term is required
                                        </p>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Submission Date
                                    </label>
                                    <input
                                        type="date"
                                        name="createdAt"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        ref={termRegister({ required: false })}
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Inforce Date
                                    </label>
                                    <input
                                        type="date"
                                        name="inforceDate"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        ref={termRegister({ required: false })}
                                    />
                                </div>

                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        onClick={resetAddTermModal}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-black text-white rounded-lg hover:bg-blue-400 hover:text-black"
                                    >
                                        Add Term
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Edit Term Modal */}
            {editTermModalState && (
                <Modal reset={resetEditTermModal}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-md w-full">
                            <div className="mb-4 text-2xl font-bold">
                                Edit Term
                            </div>
                            <form onSubmit={handleTermSubmit(handleEditTerm)}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Inforce Date
                                    </label>
                                    <input
                                        type="date"
                                        name="inforceDate"
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        ref={termRegister({ required: true })}
                                    />
                                    {termErrors.inforceDate && (
                                        <p className="text-red-500 text-xs italic">
                                            Inforce date is required
                                        </p>
                                    )}
                                </div>

                                {user.auth.type === "superadmin" && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Status
                                        </label>
                                        <Controller
                                            as={Select}
                                            name="status"
                                            control={termControl}
                                            rules={{ required: true }}
                                            options={[
                                                {
                                                    value: "active",
                                                    label: "Active",
                                                },
                                                {
                                                    value: "inactive",
                                                    label: "Inactive",
                                                },
                                                {
                                                    value: "disabled",
                                                    label: "Disabled",
                                                },
                                            ]}
                                            styles={customStyles}
                                        />
                                        {termErrors.status && (
                                            <p className="text-red-500 text-xs italic">
                                                Status is required
                                            </p>
                                        )}
                                    </div>
                                )}
                                {user.auth.type === "superadmin" && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Auto Send
                                        </label>
                                        <Controller
                                            as={Select}
                                            name="autosend"
                                            control={termControl}
                                            options={[
                                                { value: true, label: "True" },
                                                {
                                                    value: false,
                                                    label: "False",
                                                },
                                            ]}
                                            styles={customStyles}
                                        />
                                    </div>
                                )}

                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        onClick={resetEditTermModal}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-black text-white rounded-lg hover:bg-blue-400 hover:text-black"
                                    >
                                        Update Term
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default withUser(EditCase);
